import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import DfciAPI from "../apis/dfci/GeojsonAPI";
import HistoricAPI from "../apis/historicFires/HistoricAPI";
import PhysicModelAPI from "../apis/Models/PhysicModelAPI";
import MapTemplate from "../templates/map/MapTemplate";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import * as turf from "@turf/turf";
import {
  addDrawTools,
  removeDrawTools,
} from "../utils/drawTools/drawToolsCore";
import {
  getFeaturesByType,
  showDeleteModal,
} from "../utils/drawTools/drawToolsHelpers";
import { useDispatch } from "react-redux";
import {
  resetCredentials,
  setCredentials,
  setUserData,
} from "../redux/reducers/authReducer";
import { persistor } from "../redux/store/store";
import AuthAPI from "../apis/auth/AuthAPI";
import { assets } from "../publics/assets";
import useVectorTile from "../hooks/vectorTiles/useVectorTile";
import useRasterTile from "../hooks/rasterTiles/useRasterTile";
import {
  INITIAL_OPACITIES,
  LAYER_OPACITY_CONFIG,
} from "../constants/layersEntities";
import {
  DEFAULT_FUEL_MOISTURE,
  DEFAULT_TEMPERATURE,
  DEFAULT_WIND_DIRECTION,
  DEFAULT_WIND_SPEED,
  MAX_RESTRICTED_DEPARTMENTS,
} from "../constants/defaults";
import { handleExportFormat } from "../utils/exportUtils/ExportUtils";
import {
  getCurrentTimeZone,
  getCurrentFranceHour,
  getCurrentFranceDateObject,
  getLocalNowMinusOneHour,
  formatDateValue,
} from "../utils/DateTimeUtils";
import { remove3DBuildings, remove3DTerrain } from "../utils/MapboxUtils";
import {
  getPredictionColors,
  interpolateYellowToRed,
} from "../constants/colors";
import { useAccessRights } from "../hooks/userPermissions/useAccessRights";
import { formatCountryLabel } from "../utils/countryLabel";
import {
  toBackendDepartmentCode,
  fromBackendDepartmentCode,
} from "../utils/departmentCode";
import { flattenFireBuckets } from "../utils/activeFiresDateUtils";

const RASTER_LAYER_IDS = {
  soilMoisture: "soil-raster-layer",
  windSpeedData: "windSpeed-layer",
  windDirectionData: "windDirection-layer",
  precipitations: "precipitations-layer",
  hourlyPrecipitations: "hourly-precipitations-layer",
  deadFuel: "deadFuel-layer",
  temperature: "temperature-layer",
  relativeHumidity: "relative-humidity-layer",
  meanSeaLevelPressure: "mean-sea-level-pressure-layer",
  windGust: "windGust-layer",
  cloudCover: "cloud-cover-layer",
  demLevel: "dem-raster-layer",
  leafAreaIndex: "lai-raster-layer",
  vegetationHeight: "vegetation-height-raster-layer",
  biomass: "biomass-raster-layer",
  totalFuelLoad: "total-fuel-load-raster-layer",
  baresoil: "bare-soil-raster-layer",
  coniferous: "coniferous-raster-layer",
  shrubland: "shrubland-raster-layer",
  grassland: "grassland-raster-layer",
  cropland: "cropland-raster-layer",
  broadleaf: "broadleaf-raster-layer",
  pine: "pine-raster-layer",
  oak: "oak-raster-layer",
  mixedForest: "mixed-forest-raster-layer",
};
// Build the full contour layers list dynamically at the top of MapScreen:
// Max 48 contours, ordered: last contour fill/line at bottom, first at top
const buildContourLayers = (maxN = 48) => {
  const contourPart = [];
  // Add from highest index to lowest so that result1 ends up on top
  for (let n = maxN; n >= 1; n--) {
    contourPart.push(`result${n}-layer-fill`);
    contourPart.push(`result${n}-layer`);
  }
  return [
    // "cloud-cover-layer",
    // "soil-raster-layer",
    // "windGust-layer",
    // "windDirection-layer",
    // "windSpeed-layer",
    // "precipitations-layer",
    // "hourly-precipitations-layer",
    // "temperature-layer",
    // "relative-humidity-layer",
    // "mean-sea-level-pressure-layer",
    // "deadFuel-layer",
    // "dem-raster-layer",
    // "lai-raster-layer",
    // "vegetation-height-raster-layer",
    // "biomass-raster-layer",
    // "total-fuel-load-raster-layer",
    // "bare-soil-raster-layer",
    // "coniferous-raster-layer",
    // "shrubland-raster-layer",
    // "grassland-raster-layer",
    // "cropland-raster-layer",
    // "broadleaf-raster-layer",
    // "pine-raster-layer",
    // "oak-raster-layer",
    // "mixed-forest-raster-layer",

    ...contourPart,
    "input-layer",
    // buildings layers (also dynamic)
    ...Array.from({ length: maxN }, (_, i) => maxN - i).flatMap((n) => [
      `highlighted-buildings${n}-layer`,
      `highlighted-buildings${n}-symbol-layer`,
    ]),
    "historicFires-layer",

    "pools-fill-layer",
    "dfci-layer",
    "dfci-names",
    "water-layer",
    "major-roads-layer",
    "secondary-roads-layer",
    "residential-roads-layer",
    "paths-layer",
    "electricalStationsPoints-layer",
    // "electricalStationsPolygons-layer",
    "transformers-layer",
    //"crossingPoints-layer",
    "gazRefuelingStations-layer",
    "gazLines-layer",
    "railways-layer",
    "trainStations-layer",
    "departments-fill-invisible",
    "departments-layer",
    "regions-fill-invisible",
    "regions-layer",
    // "aerian-electricalLines-layer",
    "electrical-lines-layer",
    "windDirection-layer",
    // "underground-electricalLines-layer",
    "pools-symbol-layer",
    "water-symbol-layer",
    "fireStations-symbol-layer",
    "historicFires-symbol-layer",
    "activeFires-layer",
    "mainPolygons-layer",
    "startPoint-layer",
  ];
};

const MapScreen = () => {
  //-------------------- Map references--------------------
  const mapContainerRef = useRef(null); // Reference to the map container
  const mapRef = useRef(null); // Ref to store the map instance
  const drawRef = useRef(null);
  const geocoderContainerRef = useRef(null);
  const popupRef = useRef(
    new mapboxgl.Popup({
      closeButton: true,
      closeOnClick: true,
    }),
  );
  const editingRef = useRef(false); // Track whether editing is enabled
  const originalGeometryRef = useRef(null); // Store the original geometry for reset
  const markersRef = useRef(new Map()); // Track markers by featureId
  const rasterLayerOrderRef = useRef([]);
  const sentPolygonIdsRef = useRef([]);
  const prevAnyWeatherVisible = useRef(false);
  const historicFirePopupRef = useRef(null);
  const activeFirePopupRef = useRef(null);
  const handleExportRef = useRef(null);

  const {
    accessRights,
    getAllowedCountries,
    getAllowedDepartments,
    getDateRangeLimits,
  } = useAccessRights();
  const getSortedAllowedCountries = useCallback(
    (serviceName) => {
      const allowed = getAllowedCountries(serviceName);
      return allowed
        .map((key) => ({ value: key, label: formatCountryLabel(key) }))
        .sort((a, b) => a.label.localeCompare(b.label, "fr"));
    },
    [getAllowedCountries],
  );
  // Prefer "france" when it's an allowed country; otherwise first alphabetically.
  const getDefaultCountry = useCallback(
    (serviceName) => {
      const sorted = getSortedAllowedCountries(serviceName);
      return (
        sorted.find((c) => c.value === "france")?.value ??
        sorted[0]?.value ??
        ""
      );
    },
    [getSortedAllowedCountries],
  );
  const getDefaultDepartmentSelection = useCallback(
    (serviceName) => {
      const all = getAllowedDepartments(serviceName);
      return all.length > 0 && all.length < 95
        ? all.slice(0, MAX_RESTRICTED_DEPARTMENTS)
        : all;
    },
    [getAllowedDepartments],
  );
  const [selectedVegDepartment, setSelectedVegDepartment] = useState(() =>
    getDefaultCountry("display_layers_service") === "france"
      ? getDefaultDepartmentSelection("display_layers_service")
      : [],
  );
  const [selectedWeatherDepartment, setSelectedWeatherDepartment] = useState(
    () =>
      getDefaultCountry("weather") === "france"
        ? getDefaultDepartmentSelection("weather")
        : [],
  );
  //instances
  const dfciApi = useRef(new DfciAPI()).current;
  const historicApi = useRef(new HistoricAPI()).current;
  const physicModelApi = useRef(new PhysicModelAPI()).current;
  const authApi = useRef(new AuthAPI()).current;
  const dispatch = useDispatch();
  const [addedLayers, setAddedLayers] = useState([]);
  const [currentPopupFeature, setCurrentPopupFeature] = useState(null); // FOR DRAWN SHAPES
  //-----------------------------Define contour layers priority---------------------------
  const [rasterLayerOrder, setRasterLayerOrder] = useState([]);

  const contourLayers = useMemo(() => buildContourLayers(48), []);
  // --------------------Map display states--------------------
  const [selectedStyle, setSelectedStyle] = useState(
    "mapbox://styles/mapbox/satellite-streets-v11",
  );
  const [is3DTerrain, setIs3DTerrain] = useState(false); // Track whether the map is in 2D or 3D
  const [is3DBuildings, setIs3DBuildings] = useState(false); // Track whether the map is in 2D or 3D
  const [currentZoom, setCurrentZoom] = useState({});
  const [currentBounds, setCurrentBounds] = useState({
    minx: null,
    miny: null,
    maxx: null,
    maxy: null,
  });
  //----------------------------- date initialization-----------------------------------
  const currentDate = new Date();
  const formattedCurrentDate = currentDate.toISOString().split("T")[0];
  const formattedCurrentTime = currentDate
    .toTimeString()
    .split(" ")[0]
    .slice(0, 5);
  const historicDateLimits = getDateRangeLimits("historic_fire_service");
  const defaultHistoricEndDate = historicDateLimits?.dateEnd
    ? historicDateLimits.dateEnd.split("T")[0]
    : formattedCurrentDate;
  //--------------------------------------Historic fires states------------------------------

  const [filteredHistoricFires, setFilteredHistoricFires] = useState([]);
  const [filteredHistoricFiresPolygons, setFilteredHistoricFiresPolygons] =
    useState([]);
  const [selectedFiresIds, setSelectedFiresIds] = useState([]);
  const [historicFiresStartDate, setHistoricFiresStartDate] = useState("");
  const [historicFiresEndDate, setHistoricFiresEndDate] = useState(
    () => defaultHistoricEndDate,
  );
  const [showHistoricFiresList, setShowHistoricFiresList] = useState(false);
  const [showHistoricFiresByDateList, setShowHistoricFiresByDateList] =
    useState(false);
  const hasVisibleHistoricFires = filteredHistoricFiresPolygons.length > 0;
  //---------------- DFCI service layers ---------------------------------------------
  const [dfci, setDfci] = useState({
    data: null,
    show: false,
    isLoading: false,
  });
  const [railways, setRailways] = useState({
    show: false,
  });
  const [gazLines, setGazLines] = useState({
    show: false,
  });
  const [gazRefuelingStations, setGazRefuelingStations] = useState({
    show: false,
  });
  const [electricalLines, setElectricalLines] = useState({
    show: false,
  });

  const [electricalStations, setElectricalStations] = useState({
    show: false,
  });
  const [artificielWater, setArtificielWater] = useState({
    show: false,
  });
  const [urban, setUrban] = useState({
    show: false,
  });
  const [majorRoads, setMajorRoads] = useState({
    show: false,
  });
  const [fireStationsData, setFireStationsData] = useState({
    show: false,
  });
  const [trainStationsData, setTrainStationsData] = useState({
    show: false,
  });
  //----------------------------Administrative layers ------------------
  const [regions, setRegions] = useState({
    show: false,
  });
  const [departements, setDepartements] = useState({
    show: false,
  });
  //--------------------------Vegetation Layers ------------------------------
  const [vegetationCountry, setVegetationCountry] = useState(() =>
    getDefaultCountry("display_layers_service"),
  );
  const [baresoil, setBaresoil] = useState({
    show: false,
  });
  const [broadleaf, setBroadleaf] = useState({
    show: false,
  });
  const [pine, setPine] = useState({
    show: false,
  });
  const [oak, setOak] = useState({
    show: false,
  });
  const [mixedForest, setMixedForest] = useState({
    show: false,
  });
  const [biomass, setBiomass] = useState({
    show: false,
  });
  const [leafAreaIndex, setLeafAreaIndex] = useState({
    show: false,
  });
  const [totalFuelLoad, setTotalFuelLoad] = useState({
    show: false,
  });
  const [vegetationHeight, setVegetationHeight] = useState({
    show: false,
  });
  const [deadFuel, setDeadFuel] = useState({
    show: false,
  });
  const [water, setWater] = useState({
    show: false,
  });
  const [coniferous, setConiferous] = useState({
    show: false,
  });
  const [shrubland, setShrubland] = useState({
    show: false,
  });
  const [grassland, setGrassland] = useState({
    show: false,
  });
  const [cropland, setCropland] = useState({
    show: false,
  });
  const [demLevel, setDemLevel] = useState({
    show: false,
  });
  //-------------------- Weather Layers  -----------------------------
  const [precipitations, setPrecipitations] = useState({
    data: null,
    show: false,
    isLoading: false,
  });
  const [hourlyPrecipitations, setHourlyPrecipitations] = useState({
    data: null,
    show: false,
    isLoading: false,
  });
  const [temperature, setTemperature] = useState({
    data: null,
    show: false,
    isLoading: false,
  });
  const [relativeHumidity, setRelativeHumidity] = useState({
    data: null,
    show: false,
    isLoading: false,
  });
  const [meanSeaLevelPressure, setMeanSeaLevelPressure] = useState({
    data: null,
    show: false,
    isLoading: false,
  });
  const [windGust, setWindGust] = useState({
    data: null,
    show: false,
    isLoading: false,
  });
  const [cloudCover, setCloudCover] = useState({
    data: null,
    show: false,
    isLoading: false,
  });

  const [soilMoisture, setSoilMoisture] = useState({
    data: null,
    show: false,
    isLoading: false,
  });
  const [windDirectionData, setWindDirectionData] = useState({
    data: null,
    show: false,
    isLoading: false,
  });
  const [windSpeedData, setWindSpeedData] = useState({
    data: null,
    show: false,
    isLoading: false,
  });
  //------------------active fires-----------------------
  const [activeFires, setActiveFires] = useState({
    data: null,
    show: false,
    isLoading: false,
  });
  const [activeFiresStartDate, setActiveFiresStartDate] = useState(
    () => getLocalNowMinusOneHour().date,
  );
  const [activeFiresEndDate, setActiveFiresEndDate] = useState("");
  const [activeFiresStartTime, setActiveFiresStartTime] = useState(
    () => getLocalNowMinusOneHour().time,
  );
  const [activeFiresEndTime, setActiveFiresEndTime] = useState("");
  const [showActiveFiresPanel, setShowActiveFiresPanel] = useState(false);
  const [historicFires, setHistoricFires] = useState([]);
  const [activeFiresCursor, setActiveFiresCursor] = useState(0);
  const [activeFiresPreset, setActiveFiresPreset] = useState("maintenant");
  const [activeFiresDisplayStartDate, setActiveFiresDisplayStartDate] =
    useState("");
  const [activeFiresDisplayEndDate, setActiveFiresDisplayEndDate] =
    useState("");
  const activeFiresQueryMinutes = useMemo(() => {
    const time = activeFiresEndTime || activeFiresStartTime;
    if (!time) return "00";
    const [, minutes] = time.split(":");
    return minutes || "00";
  }, [activeFiresStartTime, activeFiresEndTime]);

  const activeFiresBuckets = useMemo(
    () => flattenFireBuckets(activeFires.data, activeFiresQueryMinutes),
    [activeFires.data, activeFiresQueryMinutes],
  );

  useEffect(() => {
    setActiveFiresCursor(Math.max(0, activeFiresBuckets.length - 1)); // default to latest
  }, [activeFiresBuckets.length]);

  const activeFiresCurrentBucket =
    activeFiresBuckets[activeFiresCursor] ?? null;

  const activeFiresDisplayData = useMemo(() => {
    const features = activeFiresCurrentBucket?.features;
    return {
      type: "FeatureCollection",
      features: Array.isArray(features) ? features : [],
    };
  }, [activeFiresCurrentBucket]);

  //------------------- Model output contours and responses ------------------
  const [geoJsonResults, setGeoJsonResults] = useState([]);

  const [geoJsonInputContour, setGeoJsonInputContour] = useState({
    contour: null,
    area: null,
    building: null,
    countBuildings: null,
    showBuildings: false,
  });
  const [geoJsonResultAI, setGeoJsonResultAI] = useState(null);
  const [currentLoadingMessage, setCurrentLoadingMessage] = useState(0);
  const [modelErrorMessage, setModelErrorMessage] = useState(null);
  const [buildingsLoading, setBuildingsLoading] = useState(false);
  const [resetLocationTrigger, setResetLocationTrigger] = useState(false);
  // --------------------UI Component positions/show --------------------
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 1025);
  //closeAllLayers is not automatically executed when dependencies change.
  //The dependency array of useCallback does not control execution
  //it only controls when React recreates the function reference
  const closeAllLayers = useCallback(() => {
    setRegions({ show: false });
    setDepartements({ show: false });
    setBaresoil({ show: false });
    setBroadleaf({ show: false });
    setPine({ show: false });
    setOak({ show: false });
    setMixedForest({ show: false });
    setBiomass({ show: false });
    setLeafAreaIndex({ show: false });
    setTotalFuelLoad({ show: false });
    setVegetationHeight({ show: false });
    setDeadFuel({ show: false });
    setWater({ show: false });
    setConiferous({ show: false });
    setShrubland({ show: false });
    setGrassland({ show: false });
    setCropland({ show: false });
    setDemLevel({ show: false });
    setRailways({ show: false });
    setGazLines({ show: false });
    setGazRefuelingStations({ show: false });
    setElectricalLines({ show: false });
    setElectricalStations({ show: false });
    setArtificielWater({ show: false });
    setUrban({ show: false });
    setMajorRoads({ show: false });
    setFireStationsData({ show: false });
    setTrainStationsData({ show: false });
    setSoilMoisture({ data: null, show: false, isLoading: false });
    setPrecipitations({ data: null, show: false, isLoading: false });
    setHourlyPrecipitations({ data: null, show: false, isLoading: false });
    setTemperature({ data: null, show: false, isLoading: false });
    setRelativeHumidity({ data: null, show: false, isLoading: false });
    setMeanSeaLevelPressure({ data: null, show: false, isLoading: false });
    setWindGust({ data: null, show: false, isLoading: false });
    setCloudCover({ data: null, show: false, isLoading: false });
    setWindDirectionData({ data: null, show: false, isLoading: false });
    setWindSpeedData({ data: null, show: false, isLoading: false });
    setDfci({ data: null, show: false, isLoading: false });
    setActiveFires({ data: null, show: false, isLoading: false });
    //fully reset the active-fires selection so it doesn't stay
    // stuck on a preset (e.g. "24h") with no data/timeline behind it.
    setActiveFiresPreset("maintenant");
    setActiveFiresDisplayStartDate("");
    setActiveFiresDisplayEndDate("");
    const { date: resetDate, time: resetTime } = getLocalNowMinusOneHour();
    setActiveFiresStartDate(resetDate);
    setActiveFiresStartTime(resetTime);
    setActiveFiresEndDate("");
    setActiveFiresEndTime("");
    setGeoJsonResults((prev) =>
      prev.map((r) => ({ ...r, showBuildings: false })),
    );
    clearBuildingLayers();
    setSelectedFiresIds([]);
    // Remove 3D terrain and buildings from the map
    if (mapRef.current) {
      if (is3DTerrain) {
        remove3DTerrain(mapRef.current);
      }
      if (is3DBuildings) {
        remove3DBuildings(mapRef.current);
      }
    }
    setIs3DBuildings(false);
    setIs3DTerrain(false);
    setRasterLayerOrder([]);
  }, [is3DTerrain, is3DBuildings]);
  //to track screen width
  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 1025);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const [DragComponentPosition, setDragComponentPosition] = useState({
    top: 0,
    left: 0,
  });
  const [meteoComponentPosition, setMeteoComponentPosition] = useState({
    top: 0,
    left: 0,
  });
  const [legendComponentPosition, setLegendComponentPosition] = useState({
    top: 0,
    left: 0,
  });
  const [showLayersComponent, setShowLayersComponent] = useState(false);
  const [showMeteoComponent, setShowMeteoComponent] = useState(false);
  const [sidebarPropagationOpen, setSidebarPropagationOpen] = useState(false);
  const [sidebarHistoricOpen, setSidebarHistoricOpen] = useState(false);
  const [reducedWindow, setReducedWindow] = useState({
    layersComponent: false,
    weatherComponent: false,
    eventCreationComponent: false,
    legendComponent: false,
    historicFiresComponent: false,
  });
  const [showLegendComponent, setShowLegendComponent] = useState(false);
  const [showUserComponent, setShowUserComponent] = useState(false);
  const [showNavbarMenu, setShowNavbarMenu] = useState(false);
  const [showMapStylesSidebar, setShowMapStylesSidebar] = useState(false);
  useEffect(() => {
    /* check if the screen is small so the legend component will be 
    a sidebar so it should not appear eachtime the user check a layer 
    but if it  is a draggablme component it is ok */
    if (!isSmallScreen) {
      // Check if any layer is active
      const anyLayerActive =
        regions.show ||
        departements.show ||
        soilMoisture.show ||
        windSpeedData.show ||
        baresoil.show ||
        broadleaf.show ||
        pine.show ||
        oak.show ||
        mixedForest.show ||
        totalFuelLoad.show ||
        leafAreaIndex.show ||
        biomass.show ||
        vegetationHeight.show ||
        demLevel.show ||
        water.show ||
        artificielWater.show ||
        urban.show ||
        coniferous.show ||
        shrubland.show ||
        cropland.show ||
        grassland.show ||
        majorRoads.show ||
        railways.show ||
        electricalLines.show ||
        electricalStations.show ||
        gazLines.show ||
        gazRefuelingStations.show ||
        fireStationsData.show ||
        trainStationsData.show ||
        deadFuel.show ||
        precipitations.show ||
        hourlyPrecipitations.show ||
        temperature.show ||
        relativeHumidity.show ||
        windGust.show ||
        meanSeaLevelPressure.show ||
        cloudCover.show ||
        dfci.show ||
        activeFires.show ||
        hasVisibleHistoricFires ||
        geoJsonResults.some((result) => result.showBuildings);

      // Update legend visibility only for larger screens
      setShowLegendComponent(anyLayerActive);
    }
  }, [
    isSmallScreen, // Add isSmallScreen as a dependency
    regions.show,
    departements.show,
    soilMoisture.show,
    windSpeedData.show,
    baresoil.show,
    broadleaf.show,
    pine.show,
    oak.show,
    mixedForest.show,
    totalFuelLoad.show,
    leafAreaIndex.show,
    biomass.show,
    vegetationHeight.show,
    demLevel.show,
    water.show,
    artificielWater.show,
    urban.show,
    coniferous.show,
    shrubland.show,
    cropland.show,
    grassland.show,
    majorRoads.show,
    deadFuel.show,
    electricalLines.show,
    electricalStations.show,
    railways.show,
    gazLines.show,
    gazRefuelingStations.show,
    fireStationsData.show,
    trainStationsData.show,
    precipitations.show,
    hourlyPrecipitations.show,
    temperature.show,
    relativeHumidity.show,
    meanSeaLevelPressure.show,
    windGust.show,
    cloudCover.show,
    geoJsonResults.map((r) => r.showBuildings).join(","),
    dfci.show,
    activeFires.show,
    hasVisibleHistoricFires,
  ]);
  //------------------------------layers priority----------------------------------------------
  const bringDrawLayersToTop = (map) => {
    if (!map || !map.isStyleLoaded()) return;
    const drawLayerPrefixes = ["gl-draw", ".mapbox-gl-draw", "mapbox-gl-draw"];
    const style = map.getStyle();
    if (!style?.layers) return;
    style.layers
      .filter((layer) =>
        drawLayerPrefixes.some((prefix) => layer.id.startsWith(prefix)),
      )
      .forEach((layer) => {
        map.moveLayer(layer.id);
      });
  };
  const enforceLayerOrder = (map) => {
    if (!map || !map.isStyleLoaded()) return;
    const existingLayers = contourLayers.filter((layerId) =>
      map.getLayer(layerId),
    );
    if (existingLayers.length === 0) return;
    // Move layers in the order they appear in contourLayers.
    // The last one in the list will be on top.
    existingLayers.forEach((layerId) => {
      map.moveLayer(layerId);
    });
    bringDrawLayersToTop(map);
    enforceRasterOrder(map);
  };
  const enforceRasterOrder = (map) => {
    if (!map || !map.isStyleLoaded()) return;
    const order = rasterLayerOrderRef.current;
    if (order.length === 0) return;

    const anchor = contourLayers.find((id) => map.getLayer(id));

    // Move in array order: last in array = moved last = on top
    order.forEach((layerId) => {
      if (!map.getLayer(layerId)) return;
      if (anchor && map.getLayer(anchor)) {
        map.moveLayer(layerId, anchor); // inserts below anchor, last call wins = closest below anchor = top raster
      } else {
        map.moveLayer(layerId); // no anchor fallback
      }
    });
  };
  useEffect(() => {
    if (!mapRef.current) return;
    const map = mapRef.current;

    if (!map.isStyleLoaded()) return;

    // Wait for map idle so useRasterTile has finished adding the layer
    const apply = () => enforceRasterOrder(map);

    // Try immediately (layer might already exist)
    apply();

    // Also try after idle in case layer is mid-add
    map.once("idle", apply);

    return () => {
      map.off("idle", apply);
    };
  }, [rasterLayerOrder]);
  useEffect(() => {
    if (!mapRef.current) return;
    const map = mapRef.current;

    const handleStyleData = () => {
      // Small timeout to let useRasterTile finish re-adding its layers
      setTimeout(() => enforceRasterOrder(map), 50);
    };

    map.on("styledata", handleStyleData);
    map.on("idle", () => enforceRasterOrder(map)); // catch any late additions

    return () => {
      map.off("styledata", handleStyleData);
      map.off("idle", () => enforceRasterOrder(map));
    };
  }, []); // empty deps — uses ref so always fresh
  useEffect(() => {
    rasterLayerOrderRef.current = rasterLayerOrder;
  }, [rasterLayerOrder]);
  useEffect(() => {
    if (!mapRef.current) return;
    const map = mapRef.current;

    const handleZoom = () => enforceLayerOrder(map);
    const handleStyleLoad = () => enforceLayerOrder(map);

    map.on("zoom", handleZoom); // fires DURING zoom, not just after
    map.on("zoomend", handleZoom); // keep as fallback
    map.on("style.load", handleStyleLoad);

    return () => {
      map.off("zoom", handleZoom);
      map.off("zoomend", handleZoom);
      map.off("style.load", handleStyleLoad);
    };
  }, [mapRef.current]);
  useEffect(() => {
    if (!mapRef.current) return;
    const map = mapRef.current;

    const handleLayerAdd = () => bringDrawLayersToTop(map);
    const handleStyleLoad = () => bringDrawLayersToTop(map);

    map.on("styledata", handleLayerAdd); // fires on any style/layer change
    map.on("style.load", handleStyleLoad);

    return () => {
      map.off("styledata", handleLayerAdd);
      map.off("style.load", handleStyleLoad);
    };
  }, [mapRef.current]);
  //layers animation
  const animateLayerOpacity = (
    map,
    layerIds,
    targetOpacity,
    duration = 800,
  ) => {
    const start = performance.now();

    const animate = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      // ease-out curve: feels natural, slows down at the end
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = eased * targetOpacity;

      layerIds.forEach((layerId) => {
        if (!map.getLayer(layerId)) return;
        const layer = map.getLayer(layerId);
        if (layer.type === "fill") {
          map.setPaintProperty(layerId, "fill-opacity", current);
        } else if (layer.type === "line") {
          map.setPaintProperty(layerId, "line-opacity", current);
        }
      });

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  };
  // ----------------------Initial opacities for each layer------------------------
  const buildInitialOpacities = () => {
    const base = { ...INITIAL_OPACITIES };
    for (let i = 1; i <= 48; i++) {
      base[`geoJsonResult${i}`] = 100;
    }
    return base;
  };
  const [opacities, setOpacities] = useState(() => buildInitialOpacities());
  const [resetOpacity, setResetOpacity] = useState(false);

  //------------------------------- Operation : initial situation parameters--------------------------------------
  const [originFireRef, setOriginFireRef] = useState("");
  const [originFireDate, setOriginFireDate] = useState("");
  const [originFireHour, setOriginFireHour] = useState("");
  const [originFireLocation, setOriginFireLocation] = useState("");
  const [operationId, setOperationId] = useState("");
  const [dfciCode, setDfciCode] = useState(""); // State to hold the DFCI code
  const [currentCharLength, setCurrentCharLength] = useState(2); // Track zoom level for dfci codes
  //------------------------------- Operation : Actual situation parameters---------------------
  const [windSpeed, setWindSpeed] = useState(DEFAULT_WIND_SPEED);
  const [windDirection, setWindDirection] = useState(DEFAULT_WIND_DIRECTION);
  const [fuelMoisture, setFuelMoisture] = useState(DEFAULT_FUEL_MOISTURE);
  const [temperatureParam, setTemperatureParam] = useState(DEFAULT_TEMPERATURE);
  const [isOperationRunning, setIsOperationRunning] = useState(false);

  const [contourHour, setContourHour] = useState("");
  const [contourDate, setContourDate] = useState("");
  // const [selectedOptionModel, setSelectedOptionModel] = useState("");
  const [selectedMeteoGetOption, setSelectedMeteoGetOption] = useState(
    "Réelles par satellite",
  );
  const [predictionHours, setPredictionHours] = useState(4);
  const [timeStep, setTimeStep] = useState("1h");
  const predictionColors = useMemo(() => {
    return getPredictionColors(
      predictionHours,
      timeStep,
      geoJsonResults.length,
    );
  }, [predictionHours, timeStep, geoJsonResults.length]);
  //ensures the arrays of default parzams (default 4 length) are always pre-padded to the right length before any modal interaction
  useEffect(() => {
    const expectedLength = predictionHours;

    const padArr = (arr, setter) => {
      if (arr.length === expectedLength) return;
      const result = [];
      for (let i = 0; i < expectedLength; i++) {
        if (i < arr.length && arr[i] !== undefined && arr[i] !== null) {
          result.push(arr[i]);
        } else {
          result.push(result[result.length - 1] ?? arr[0] ?? 0); // fill gaps with last known value
        }
      }
      setter(result); // update the state with clean full-length array
    };

    padArr(windSpeed, setWindSpeed);
    padArr(windDirection, setWindDirection);
    padArr(fuelMoisture, setFuelMoisture);
    padArr(temperatureParam, setTemperatureParam);
  }, [predictionHours]); // ONLY when predictionHours changes

  //----------------------------------------- Time Functions ------------------------------------
  const getDtMinutes = (step) =>
    ({ "1h": 60, "30min": 30 /* , "45min": 45 */, "15min": 15 })[step] || 60;

  const isHourBoundary = (idx, timeStep) => {
    const dtMinutes = getDtMinutes(timeStep);
    const contoursPerHour = 60 / dtMinutes;
    return (idx + 1) % contoursPerHour === 0; // true for last contour of each hour
  };

  useEffect(() => {
    const detectedZone = getCurrentTimeZone();
    setCurrentTimeZone(detectedZone);
  }, []);
  //-----------------------------------------WEATHER TIME & TIMELINE MANAGEMENT------------------------------------
  const [weatherCountry, setWeatherCountry] = useState(() =>
    getDefaultCountry("weather"),
  );
  const [weatherRequestDate, setWeatherRequestDate] = useState(
    getCurrentFranceDateObject(),
  );
  const [weatherRequestStartHour, setWeatherRequestStartHour] = useState(
    getCurrentFranceHour(),
  );
  const [sliderIndex, setSliderIndex] = useState(0); // Represents the current hour index
  const [timelineKey, setTimelineKey] = useState(0);
  const getWeatherDateTime = () => {
    const totalHours = weatherRequestStartHour + sliderIndex;
    const hour = totalHours % 24;
    const daysOffset = Math.floor(totalHours / 24);

    const base = new Date(
      parseInt(weatherRequestDate.year, 10),
      parseInt(weatherRequestDate.month, 10) - 1,
      parseInt(weatherRequestDate.day, 10),
    );
    base.setDate(base.getDate() + daysOffset);

    const day = base.getDate().toString().padStart(2, "0");
    const month = (base.getMonth() + 1).toString().padStart(2, "0");
    const year = base.getFullYear().toString();
    const time = `${hour.toString().padStart(2, "0")}:00`;

    return {
      dateString: `${day}-${month}-${year}`,
      timeString: time,
    };
  };

  const { dateString, timeString } = getWeatherDateTime();
  const [selectedTime, setSelectedTime] = useState("16:00");
  const [currentTimeZone, setCurrentTimeZone] = useState("");
  useEffect(() => {
    const detectedZone = getCurrentTimeZone();
    setCurrentTimeZone(detectedZone);
  }, []); // already exists

  // ADD THIS BLOCK RIGHT AFTER:
  const anyWeatherVisible =
    windSpeedData.show ||
    windDirectionData.show ||
    precipitations.show ||
    hourlyPrecipitations.show ||
    deadFuel.show ||
    temperature.show ||
    relativeHumidity.show ||
    meanSeaLevelPressure.show ||
    cloudCover.show ||
    windGust.show;

  useEffect(() => {
    if (anyWeatherVisible && !prevAnyWeatherVisible.current) {
      setSliderIndex(0);
      setTimelineKey((prev) => prev + 1);
    }
    prevAnyWeatherVisible.current = anyWeatherVisible;
  }, [anyWeatherVisible]);
  //---------------------------------acces permissions ( allowed countries) -----------------------------
  useEffect(() => {
    if (!accessRights) return;

    if (!vegetationCountry) {
      const defaultCountry = getDefaultCountry("display_layers_service");
      if (defaultCountry) {
        setVegetationCountry(defaultCountry);
        if (defaultCountry === "france") {
          setSelectedVegDepartment(
            getDefaultDepartmentSelection("display_layers_service"),
          );
        }
      }
    }
    if (!weatherCountry) {
      const defaultCountry = getDefaultCountry("weather");
      if (defaultCountry) {
        setWeatherCountry(defaultCountry);
        if (defaultCountry === "france") {
          setSelectedWeatherDepartment(
            getDefaultDepartmentSelection("weather"),
          );
        }
      }
    }
  }, [
    accessRights,
    getDefaultCountry,
    getDefaultDepartmentSelection,
    vegetationCountry,
    weatherCountry,
  ]);

  // Safety net: if country is france but departments ended up empty
  useEffect(() => {
    if (!accessRights) return;
    if (vegetationCountry === "france" && selectedVegDepartment.length === 0) {
      const def = getDefaultDepartmentSelection("display_layers_service");
      if (def?.length) setSelectedVegDepartment(def);
    }
    if (weatherCountry === "france" && selectedWeatherDepartment.length === 0) {
      const def = getDefaultDepartmentSelection("weather");
      if (def?.length) setSelectedWeatherDepartment(def);
    }
  }, [
    accessRights,
    vegetationCountry,
    weatherCountry,
    getDefaultDepartmentSelection,
    selectedVegDepartment,
    selectedWeatherDepartment,
  ]);
  //-----------------------------------user Historic Events List & settings preferences ------------------------------
  const [userHistoricEventsList, setUserHistoricEventsList] = useState({
    data: [],
    show: false,
    polygons: [],
    selectedEventId: 0,
    isLoading: false,
  });
  const [loadingEventId, setLoadingEventId] = useState(null);

  const [showClock, setShowClock] = useState(false);
  const [showCursorCoords, setShowCursorCoords] = useState(false);

  //--------------------------Layer Opacity Management Functions----------------------------
  const fullOpacityConfig = { ...LAYER_OPACITY_CONFIG };
  for (let i = 1; i <= 48; i++) {
    fullOpacityConfig[`result${i}-layer-fill`] = `geoJsonResult${i}`;
  }
  const handleResetOpacity = () => {
    setResetOpacity((prev) => !prev);
  };
  const handleOpacityChange = (layerName, newOpacity) => {
    setOpacities((prev) => ({
      ...prev,
      [layerName]: newOpacity,
    }));
  };

  // Update opacity for the GeoJSON layer whenever slider value changes
  useEffect(() => {
    if (!mapRef.current) return;
    const map = mapRef.current;

    // Update all layers' opacity in one pass
    Object.entries(fullOpacityConfig).forEach(([layerId, config]) => {
      if (!map.getLayer(layerId)) return;

      // Handle both old (string) and new (object) config formats
      const opacityKey = typeof config === "string" ? config : config.key;
      const opacityProperty =
        typeof config === "object" && config.type === "raster"
          ? "raster-opacity"
          : "fill-opacity";

      map.setPaintProperty(
        layerId,
        opacityProperty,
        opacities[opacityKey] / 100,
      );
    });
  }, [opacities]);
  useEffect(() => {
    const resetValues = {};
    for (let i = 1; i <= 48; i++) {
      resetValues[`geoJsonResult${i}`] = 100;
    }
    setOpacities((prev) => ({ ...prev, ...resetValues }));
  }, [resetOpacity]);
  //-----------------------------operation : WEATHER SIMULATION CHANGE HANDLERS--------------------------
  const handleWindSpeedChange = (index, newValue) => {
    const updatedWindSpeed = [...windSpeed];
    updatedWindSpeed[index] = parseFloat(newValue) || 0;
    setWindSpeed(updatedWindSpeed);
  };

  const handleWindDirectionChange = (index, newValue) => {
    const updatedWindDirection = [...windDirection];
    updatedWindDirection[index] = parseInt(newValue) || 0;
    setWindDirection(updatedWindDirection);
  };

  const handleFuelMoistureChange = (index, newValue) => {
    const updatedFuelMoisture = [...fuelMoisture];
    updatedFuelMoisture[index] = parseInt(newValue) || 0;
    setFuelMoisture(updatedFuelMoisture);
  };
  const handleTemperatureChange = (index, newValue) => {
    const updatedTemperature = [...temperatureParam];
    updatedTemperature[index] = parseFloat(newValue) || 0;
    setTemperatureParam(updatedTemperature);
  };
  //------------------------------Fire Simulation Functions---------------------------
  // Function to get only the polygons from drawn shapes (GeoJSON)
  const getPolygons = () => {
    return getFeaturesByType(drawRef, "Polygon");
  };
  // Function to get only the points from drawn shapes (GeoJSON)
  const getPoints = () => {
    return getFeaturesByType(drawRef, "Point");
  };
  // Function to clear existing GeoJSON layers
  const clearGeoJsonLayers = () => {
    if (!mapRef.current) return;
    const map = mapRef.current;

    // Supprimer les couches enregistrées dans addedLayers (méthode existante)
    addedLayers.forEach(({ layerId, sourceId }) => {
      const fillLayerId = `${layerId}-fill`;
      if (map.getLayer(fillLayerId)) map.removeLayer(fillLayerId);
      if (map.getLayer(layerId)) map.removeLayer(layerId);
      if (map.getSource(sourceId)) map.removeSource(sourceId);
    });
    setAddedLayers([]);

    // Nettoyage supplémentaire : toutes les sources/layers de contours
    for (let n = 1; n <= 48; n++) {
      const sourceId = `result${n}-source`;
      const fillLayerId = `result${n}-layer-fill`;
      const lineLayerId = `result${n}-layer`;
      if (map.getLayer(fillLayerId)) map.removeLayer(fillLayerId);
      if (map.getLayer(lineLayerId)) map.removeLayer(lineLayerId);
      if (map.getSource(sourceId)) map.removeSource(sourceId);
    }

    // Source du contour initial
    // if (map.getLayer("input-layer")) map.removeLayer("input-layer");
    // if (map.getSource("input-source")) map.removeSource("input-source");
  };
  const PERMISSION_ERROR_TEXT =
    "Access denied: this outline is outside your department";

  const classifyBackendError = ({ chunk, error }) => {
    const rawData = error?.response?.data;
    const backendMessage = (
      chunk?.message ||
      chunk?.detail ||
      (typeof rawData === "string"
        ? rawData
        : rawData?.message || rawData?.detail) ||
      ""
    ).trim();

    if (backendMessage.startsWith(PERMISSION_ERROR_TEXT)) {
      return { type: "permission", message: backendMessage };
    }
    return { type: "generic" };
  };
  // Function to handle sending polygons to the backend
  const handleSendPolygons = async () => {
    setIsOperationRunning(true);

    const polygons = getPolygons();
    const points = getPoints();
    sentPolygonIdsRef.current = polygons.map((p) => p.id);
    const getDtInMinutes = (step) => {
      const map = { "1h": 60, "30min": 30, "45min": 45, "15min": 15 };
      return map[step] || 60;
    };

    const getDtInHours = (step) => {
      const map = { "1h": 1, "30min": 0.5, "45min": 0.75, "15min": 0.25 };
      return map[step] || 1;
    };

    // Enforce simulation mode → only 1h allowed
    const effectiveTimeStep =
      selectedMeteoGetOption === "Ajuster les données météo" ? "1h" : timeStep;
    const dtMinutes = getDtInMinutes(effectiveTimeStep);
    const dtHours = getDtInHours(effectiveTimeStep);
    const totalPolygons = Math.ceil(predictionHours / dtHours);

    const allowedCountries = getAllowedCountries("physical_model");
    const allowedDepartments = getAllowedDepartments("physical_model");
    const normalizedDepartments = allowedDepartments.map(
      toBackendDepartmentCode,
    );
    const padArrayToLength = (arr, expectedLength, defaultValue) => {
      if (!arr || arr.length === 0) {
        return Array(expectedLength).fill(defaultValue);
      }
      if (arr.length >= expectedLength) {
        return arr.slice(0, expectedLength);
      }
      const padded = [...arr];
      const lastValue = arr[arr.length - 1] ?? defaultValue;
      while (padded.length < expectedLength) {
        padded.push(lastValue);
      }
      return padded;
    };

    // 👇 PAD THE ARRAYS TO MATCH totalPolygons (predictionHours + 1)
    const expectedLength = predictionHours;

    const paddedWindSpeed = padArrayToLength(
      windSpeed,
      expectedLength,
      DEFAULT_WIND_SPEED[0] || 31,
    );
    const paddedWindDirection = padArrayToLength(
      windDirection,
      expectedLength,
      DEFAULT_WIND_DIRECTION[0] || 114,
    );
    const paddedFuelMoisture = padArrayToLength(
      fuelMoisture,
      expectedLength,
      DEFAULT_FUEL_MOISTURE[0] || 10,
    );
    const paddedTemperature = padArrayToLength(
      temperatureParam,
      expectedLength,
      DEFAULT_TEMPERATURE[0] || 20,
    );

    // Create blobs for polygons and points
    const polygonsBlob = new Blob(
      [JSON.stringify({ type: "FeatureCollection", features: polygons })],
      { type: "application/json" },
    );
    const pointsBlob = new Blob(
      [JSON.stringify({ type: "FeatureCollection", features: points })],
      { type: "application/json" },
    );

    // Create a single FormData object and append both files
    // NOTE: allowed_countries / allowed_departments are NOT sent via FormData anymore.
    // The backend expects them as query params (country=..., department=...&department=...),
    // so they're now included in sharedParams below instead.
    const formData = new FormData();
    formData.append("geojson_polygons", polygonsBlob, "polygons.geojson");
    formData.append("geojson_points", pointsBlob, "points.geojson");

    // to create URLSearchParams
    const createParams = (params) => {
      const urlParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((v) => urlParams.append(key, v));
        } else {
          urlParams.append(key, value);
        }
      });
      return urlParams;
    };

    const sharedParams = {
      utc: currentTimeZone,
      time_first_outline: contourHour,
      date_first_outline: contourDate,
      ref: originFireRef,
      date: originFireDate,
      time: originFireHour,
      location: originFireLocation,
      dfci: dfciCode,
      nb_of_polygons: totalPolygons,
      dt_polygons: dtMinutes,
      country: allowedCountries,
      department: normalizedDepartments,
    };

    // 👇 USE PADDED ARRAYS FOR SIMULATION
    const params_simulation = createParams({
      ...sharedParams,
      wind_speed: paddedWindSpeed,
      wind_direction: paddedWindDirection,
      fuelmoisture: paddedFuelMoisture,
      temperature: paddedTemperature,
    });

    const params_dynamic = createParams(sharedParams);

    try {
      // Reset loading message at start
      setCurrentLoadingMessage(0);
      clearGeoJsonLayers();
      clearBuildingLayers();
      resetGeoJsonResults();
      setModelErrorMessage(null);
      setBuildingsLoading(true);
      let response;

      if (selectedMeteoGetOption === "Ajuster les données météo") {
        if (operationId) {
          // Update simulation data - ALSO USE PADDED ARRAYS HERE
          const params_simul_update = createParams({
            ...sharedParams,
            id: operationId,
            wind_speed: paddedWindSpeed,
            wind_direction: paddedWindDirection,
            fuelmoisture: paddedFuelMoisture,
            temperature: paddedTemperature,
            dt_polygons: 60,
          });
          response = await physicModelApi.putSimulationDataStream(
            formData,
            params_simul_update,
          );
        } else {
          // Create new simulation data
          response = await physicModelApi.postSimulationDataStream(
            formData,
            params_simulation,
          );
        }
      } else if (selectedMeteoGetOption === "Réelles par satellite") {
        if (operationId) {
          const params_dynamic_update = createParams({
            ...sharedParams,
            id: operationId,
          });
          response = await physicModelApi.putDynamicDataStream(
            formData,
            params_dynamic_update,
          );
        } else {
          response = await physicModelApi.postDynamicDataStream(
            formData,
            params_dynamic,
          );
        }
      }
      if (response?.status === 200) {
        const reader = response.data.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let inputContour = null;
        const contours = {};

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop();

          for (const line of lines) {
            if (!line.trim()) continue;
            const chunk = JSON.parse(line);

            if (chunk.event === "init") {
              inputContour = chunk.input;
              setOperationId(String(chunk.fire_id));
              setGeoJsonInputContour((prev) => ({
                ...prev,
                contour: chunk.input,
                area: chunk.area0,
              }));
            }

            if (chunk.event === "polygon") {
              const order = chunk.order;
              contours[order] = chunk.geojson;
              setGeoJsonResults((prev) => {
                const updated = [...prev];
                updated[order - 1] = {
                  contour: chunk.geojson,
                  area: chunk.area,
                  building: null,
                  countBuildings: null,
                  showBuildings: false,
                  showContour: true,
                };
                return updated;
              });
              setCurrentLoadingMessage(order);
            }

            if (chunk.event === "done") {
              const totalPolygonsCount = Object.keys(contours).length;
              try {
                const buildingsFormData = new FormData();
                const inputBlob = new Blob(
                  [
                    JSON.stringify({
                      type: "FeatureCollection",
                      features: inputContour.features || inputContour,
                    }),
                  ],
                  { type: "application/json" },
                );
                buildingsFormData.append("input", inputBlob, "input.geojson");

                for (let i = 1; i <= totalPolygonsCount; i++) {
                  const spreadGeoJSON = contours[i];
                  const spreadBlob = new Blob(
                    [
                      JSON.stringify({
                        type: "FeatureCollection",
                        features: spreadGeoJSON.features || spreadGeoJSON,
                      }),
                    ],
                    { type: "application/json" },
                  );
                  buildingsFormData.append(
                    "spreads",
                    spreadBlob,
                    `spread${i}.geojson`,
                  );
                }
                buildingsFormData.append(
                  "allowed_countries",
                  JSON.stringify(allowedCountries),
                );
                buildingsFormData.append(
                  "allowed_departments",
                  JSON.stringify(normalizedDepartments),
                );
                setBuildingsLoading(true);
                const buildingsResponse =
                  await physicModelApi.postBuildingsData(
                    buildingsFormData,
                    chunk.fire_id,
                  );

                if (buildingsResponse?.status === 200) {
                  const data = buildingsResponse.data.data;
                  setGeoJsonInputContour((prev) => ({
                    ...prev,
                    building: data.build0,
                    countBuildings: data.count0,
                  }));

                  for (let i = 1; i <= totalPolygonsCount; i++) {
                    setGeoJsonResults((prev) => {
                      const updated = [...prev];
                      if (updated[i - 1]) {
                        updated[i - 1] = {
                          ...updated[i - 1],
                          building: data[`build${i}`],
                          countBuildings: data[`count${i}`],
                        };
                      }
                      return updated;
                    });
                  }
                }
              } catch (err) {
                // console.log(
                //   "RAW BUILDINGS ERROR:",
                //   err?.response?.status,
                //   err?.response?.data,
                // );

                console.error("Buildings API Error:", err);
                setModelErrorMessage(classifyBackendError({ error: err }));
              } finally {
                setBuildingsLoading(false);
                setIsOperationRunning(false);
              }
            }
            if (chunk.event === "error") {
              setModelErrorMessage(classifyBackendError({ chunk }));
              setBuildingsLoading(false);
            }
          }
        }
      }
      if (!response.data || response.status !== 200) {
        console.error(
          "Failed to send polygons, response status:",
          response.data ? response.status : "No response",
        );
      }
    } catch (error) {
      // console.log(
      //   "RAW ERROR RESPONSE:",
      //   error?.response?.status,
      //   error?.response?.data,
      // );

      setModelErrorMessage(classifyBackendError({ error }));
      setBuildingsLoading(false);
    }
  };
  const sanitizeForFilename = (str) =>
    (str || "")
      .toString()
      .trim()
      .replace(/[^a-zA-Z0-9-_]+/g, "_")
      .replace(/^_+|_+$/g, "");

  const buildExportFileName = () => {
    const type =
      selectedMeteoGetOption === "Ajuster les données météo"
        ? "sim"
        : "dynamic";

    // Prefer the operation's own date, fall back to the origin fire date,
    // then to today's date if neither is set yet.
    const rawDate = originFireDate || contourDate || formattedCurrentDate;
    const date = sanitizeForFilename(rawDate);

    const ref =
      sanitizeForFilename(originFireRef) || operationId || "operation";

    return `${ref}_${type}_${date}`;
  };
  const buildHistoricExportGroups = () => {
    const historicPolygons = userHistoricEventsList.polygons || [];
    if (historicPolygons.length === 0) return [];

    const mainPolygons = historicPolygons.filter((p) => p.type === "main");
    const predictionPolygons = historicPolygons.filter(
      (p) => p.type === "predictions",
    );
    const startPoints = historicPolygons.filter((p) => p.type === "start");

    const flatten = (items) =>
      items.flatMap((p) => p.features.flatMap((fc) => fc.features));

    const groups = [];

    if (mainPolygons.length > 0) {
      groups.push({
        styleId: "historic_main",
        styleConfig: { color: "ff0000ff", width: 3 },
        label: "Historique - Contour initial",
        features: flatten(mainPolygons),
      });
    }

    const predictionsByOrder = {};
    predictionPolygons.forEach((p) => {
      if (!predictionsByOrder[p.order]) predictionsByOrder[p.order] = [];
      predictionsByOrder[p.order].push(p);
    });

    Object.entries(predictionsByOrder)
      .sort(([a], [b]) => Number(a) - Number(b))
      .forEach(([order, items]) => {
        groups.push({
          styleId: `historic_prediction_${order}`,
          styleConfig: { color: "ff0045ff", width: 2 },
          label: `Historique - t+${order}h`,
          features: flatten(items),
        });
      });

    if (startPoints.length > 0) {
      groups.push({
        styleId: "historic_start",
        styleConfig: { color: "ff000000", width: 1 },
        label: "Historique - Point de départ",
        features: flatten(startPoints),
      });
    }

    return groups;
  };

  const handleExport = async (
    format = "geojson",
    { includeHistoric = false } = {},
  ) => {
    const draw = drawRef.current;
    const features = draw.getAll();

    const polygons = features.features.filter(
      (f) =>
        f.geometry.type === "Polygon" &&
        !sentPolygonIdsRef.current.includes(f.id),
    );
    const points = features.features.filter((f) => f.geometry.type === "Point");

    const extractFeatures = (contour, label) => {
      if (!contour) return [];
      let feats = [];
      if (contour.type === "FeatureCollection") feats = contour.features ?? [];
      else if (contour.type === "Feature") feats = [contour];
      else if (Array.isArray(contour)) feats = contour;
      return feats.map((f) => ({
        ...f,
        properties: { ...f.properties, contour_label: label },
      }));
    };

    const flattenedPredictedContours = [
      ...extractFeatures(geoJsonInputContour.contour, "Contour initial"),
      ...geoJsonResults.flatMap((result, idx) =>
        extractFeatures(result.contour, `t+${idx + 1}h`),
      ),
    ];

    const historicGroups = includeHistoric ? buildHistoricExportGroups() : [];
    const flattenedHistoricFeatures = historicGroups.flatMap((g) =>
      g.features.map((f) => ({
        ...f,
        properties: { ...f.properties, contour_label: g.label },
      })),
    );

    const combinedFeatures = [
      ...polygons,
      ...points,
      ...flattenedPredictedContours,
      ...flattenedHistoricFeatures,
    ];

    await handleExportFormat(format, {
      combinedFeatures,
      geoJsonInputContour,
      geoJsonResults,
      drawnPolygons: polygons,
      drawnPoints: points,
      extraGroups: historicGroups,
      fileName: buildExportFileName(),
    });
  };
  useEffect(() => {
    handleExportRef.current = (format) =>
      handleExport(format, { includeHistoric: true });
  });
  const showAllBuildings = () => {
    setGeoJsonResults((prev) =>
      prev.map((result, idx) => ({
        ...result,
        showBuildings: result.building ? true : false,
      })),
    );
  };

  const hideAllBuildings = () => {
    setGeoJsonResults((prev) =>
      prev.map((result) => ({
        ...result,
        showBuildings: false,
      })),
    );
  };

  const clearBuildingLayers = () => {
    if (!mapRef.current) return;
    const map = mapRef.current;
    for (let n = 1; n <= 48; n++) {
      const fillLayerId = `highlighted-buildings${n}-layer`;
      const symbolLayerId = `highlighted-buildings${n}-symbol-layer`;
      const sourceId = `highlighted-buildings${n}`;
      if (map.getLayer(fillLayerId)) map.removeLayer(fillLayerId);
      if (map.getLayer(symbolLayerId)) map.removeLayer(symbolLayerId);
      if (map.getSource(sourceId)) map.removeSource(sourceId);
    }
  };
  const resetGeoJsonResults = () => {
    setGeoJsonResults([]);
    setGeoJsonInputContour({
      contour: null,
      area: null,
      building: null,
      countBuildings: null,
      showBuildings: false,
    });
  };
  const handleReset = () => {
    // Reset the states associated with the origin fire form
    setOriginFireRef(""); // Reset reference
    setOriginFireDate(formattedCurrentDate); // Reset date
    setOriginFireHour(""); // Reset hour
    setOriginFireLocation(""); // Reset location
    setResetLocationTrigger((prev) => !prev);
    setDfciCode(""); // Reset DFCI code
    resetGeoJsonResults();
    setGeoJsonResultAI(null); // Reset AI model results
    setPredictionHours(4);
    setTimeStep("1h");
    setOperationId(""); // Reset operation ID
    clearGeoJsonLayers(); // Clear GeoJSON layers on the map

    if (mapRef.current) {
      // Nettoyage renforcé (au cas où)
      for (let n = 1; n <= 48; n++) {
        const sourceId = `result${n}-source`;
        const fillLayerId = `result${n}-layer-fill`;
        const lineLayerId = `result${n}-layer`;
        if (mapRef.current.getLayer(fillLayerId))
          mapRef.current.removeLayer(fillLayerId);
        if (mapRef.current.getLayer(lineLayerId))
          mapRef.current.removeLayer(lineLayerId);
        if (mapRef.current.getSource(sourceId))
          mapRef.current.removeSource(sourceId);
      }
    }

    // setSelectedOptionModel("");
    setSelectedMeteoGetOption("Réelles par satellite");
    setContourHour("");
    setContourDate("");
    setWindSpeed([0.0, 0.0, 0.0, 0.0]);
    setWindDirection([0, 0, 0, 0]);
    setFuelMoisture([0, 0, 0, 0]);
    setTemperatureParam([0, 0, 0, 0]);
    setIsOperationRunning(false);
    sentPolygonIdsRef.current = [];
    // Clear all drawn polygons using Mapbox GL Draw
    if (drawRef?.current) {
      drawRef.current.deleteAll(); // Clear all drawn features
    }

    // Remove all custom markers (star icons)
    if (markersRef.current) {
      markersRef.current.forEach((marker) => {
        marker.remove(); // Remove each marker
      });
      markersRef.current.clear(); // Clear the markers reference
    }
  };
  //----------------------------- User ptofilr functions --------------------------------
  const getUserData = async () => {
    try {
      const response = await authApi.getUserDataAPI();
      if (response.status === 200) {
        dispatch(
          setUserData({
            user: response.data.data,
          }),
        );
        //console.log("user data", response.data.data);
      }
    } catch (error) {
      console.error("get user data failed", error);
    }
  };
  const logout = async () => {
    try {
      await authApi.logoutAPI(); // wait for backend logout
    } catch (error) {
      console.error("Logout API failed", error);
    }
    dispatch(resetCredentials()); // Then reset Redux state
    persistor.purge(); // Clears persisted data
  };

  const updateProfile = async (formData) => {
    try {
      const response = await authApi.updateProfileAPI(formData);
      const newToken = response.headers["x-access-token"];

      dispatch(
        setCredentials({
          user: response.data.user,
          accessToken: newToken,
        }),
      );
    } catch (error) {
      console.error("Error updating profile:", error);
    }
  };
  const updatePassword = async (password, newPassword, id) => {
    try {
      const response = await authApi.updatePasswordAPI(
        password,
        newPassword,
        id,
      );
      // return response.data;
    } catch (error) {
      console.error("Errror updating password", error);
    }
  };
  //--------------------------------------UI CONTORL/SHOW FUNCTIONS-----------------------------------
  const openOperatinalFlow = () => {
    if (sidebarHistoricOpen) {
      setSidebarHistoricOpen(false);
    }
    setSidebarPropagationOpen(!sidebarPropagationOpen);
  };
  const openHistoricSidebar = () => {
    if (sidebarPropagationOpen) {
      setSidebarPropagationOpen(false);
    }
    setSidebarHistoricOpen(!sidebarHistoricOpen);
  };
  //handle Draggable Component Toggle and position
  const handleDraggableComponentToggle = (event) => {
    setShowLayersComponent((prev) => !prev);
    if (!isSmallScreen) {
      setDragComponentPosition({ top: event.clientY, left: event.clientX });
    }
  };

  const handleMeteoComponentToggle = (event) => {
    setShowMeteoComponent(!showMeteoComponent);
    if (!isSmallScreen) {
      setMeteoComponentPosition({ top: event.clientY, left: event.clientX });
    }
  };
  const handleLegendComponentToggle = (event) => {
    setShowLegendComponent((prev) => !prev);
    if (!isSmallScreen) {
      setLegendComponentPosition({ top: event.clientY, left: event.clientX });
    }
  };
  const toggleMapStylesSidebar = () => {
    setShowMapStylesSidebar(!showMapStylesSidebar);
  };
  const onShowUserComponent = async () => {
    await getUserData();
    setShowUserComponent(!showUserComponent);
  };
  const onShowNavbarMenu = () => {
    setShowNavbarMenu(!showNavbarMenu);
  };
  //--------------------------------  DFCI CODE SEARCH BAR HANDLING AND AUTO ZOOMING --------------------------------
  // Function to zoom to a specific DFCI grid
  const onZoomToGrid = (code) => {
    if (!dfci.data || !Array.isArray(dfci.data.features)) {
      console.error("DFCI data is not available or malformed.");
      return;
    }

    if (!code || code.length < 2) {
      console.error(
        "Invalid DFCI code. Please enter a code with at least 2 characters.",
      );
      return;
    }

    let foundGrid = null;
    let targetDfciCode = "";
    let lastBounds = null;

    for (let i = 2; i <= code.length; i += 2) {
      targetDfciCode = code.slice(0, i);
      foundGrid = dfci.data.features.find(
        (feature) =>
          feature.properties.NOM.toLowerCase() === targetDfciCode.toLowerCase(),
      );

      if (foundGrid) {
        const coordinates = foundGrid.geometry.coordinates[0][0];
        const bounds = coordinates.reduce(
          (bounds, coord) => bounds.extend([coord[0], coord[1]]),
          new mapboxgl.LngLatBounds(),
        );

        if (mapRef.current) {
          mapRef.current.fitBounds(bounds, { padding: 50 });
        }

        lastBounds = bounds;
      }
    }

    if (!foundGrid) {
      console.error("DFCI grid code not found:", code);
    }
  };

  useEffect(() => {
    if (dfciCode.length >= currentCharLength) {
      const targetCode = dfciCode.slice(0, currentCharLength);
      onZoomToGrid(targetCode);

      if (currentCharLength < dfciCode.length) {
        setTimeout(() => setCurrentCharLength((prev) => prev + 2), 500);
      }
    }
  }, [currentCharLength, dfciCode]);

  // Reset the current zoom level when the DFCI code changes
  useEffect(() => {
    setCurrentCharLength(2);
  }, [dfciCode]);
  //--------------------------------------------- LAYERS APIS CALL FUNCTIONS ---------------------------
  const getDfciGrid = async (zoomLevel, minx, miny, maxx, maxy) => {
    try {
      const response = await dfciApi.getDfciGridApi(
        zoomLevel,
        minx,
        miny,
        maxx,
        maxy,
      );
      if (response.status === 200) {
        setDfci((prev) => ({
          ...prev,
          data: response.data.data,
        }));
        //  console.log("dfci header: ", response.headers);
      }
    } catch (error) {
      console.error("Error fetching DFCI data:", error);
    }
  };
  const getActiveFires = async (startDate, endDate, startTime, endTime) => {
    setActiveFires((prev) => ({ ...prev, isLoading: true }));
    try {
      const response = await historicApi.getActiveFiresApi(
        startDate,
        endDate,
        startTime,
        endTime,
      );
      if (response.status === 200) {
        setActiveFires((prev) => ({
          ...prev,
          data: response.data,
          isLoading: false,
        }));
        console.log(" active fires data:", response.data);
      }
    } catch (error) {
      console.error("Error fetching active fires data:", error);
      setActiveFires((prev) => ({ ...prev, isLoading: false }));
    }
  };
  //hISTORIC FIRES
  const getHistoricFires = async (overrideCountry, overrideDepartments) => {
    try {
      const country =
        overrideCountry ?? getDefaultCountry("historic_fire_service");
      const rawDepartments =
        country === "france"
          ? (overrideDepartments ??
            getAllowedDepartments("historic_fire_service"))
          : [];
      const departmentsForBackend = rawDepartments.map(toBackendDepartmentCode);
      const dateLimits = getDateRangeLimits("historic_fire_service");

      // End date: his allowed end date, or today if he has no upper bound.
      const effectiveDateTo = dateLimits?.dateEnd
        ? dateLimits.dateEnd.split("T")[0]
        : formattedCurrentDate;
      // Start date: his allowed start date, or null (no restriction) if he
      // has full access from the beginning.
      const effectiveDateFrom = dateLimits?.dateStart
        ? dateLimits.dateStart.split("T")[0]
        : null;

      const response = await historicApi.getAllHistoricFiresApi({
        country,
        departments: departmentsForBackend,
        dateFrom: effectiveDateFrom,
        dateTo: effectiveDateTo,
      });
      if (response.status === 200) {
        setHistoricFires(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching Historic fires data:", error);
    }
  };
  const getHistoricFiresByDate = async (
    startDate,
    endDate,
    country,
    departments,
  ) => {
    try {
      const departmentsForBackend = Array.isArray(departments)
        ? departments.map(toBackendDepartmentCode)
        : [];

      const response = await historicApi.getHistoricFiresByDateApi(
        startDate,
        endDate,
        country,
        departmentsForBackend,
      );

      if (response.status === 200) {
        setFilteredHistoricFires(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching filtered historic fires:", error);
    }
  };
  const getHistoricFiresByIds = async (ids) => {
    try {
      const response = await historicApi.getHistoricFiresByIdsApi(ids);
      if (response.status === 200) {
        const data = response.data.data;
        setFilteredHistoricFiresPolygons(data);
      }
    } catch (error) {
      console.error("Error fetching filtered historic fires polygons:", error);
    }
  };
  const mergeGeoJSON = (geoJSONList) => {
    const merged = {
      type: "FeatureCollection",
      features: [],
    };

    geoJSONList.forEach((geoJSON) => {
      if (
        geoJSON.type === "FeatureCollection" &&
        Array.isArray(geoJSON.features)
      ) {
        merged.features.push(...geoJSON.features);
      }
    });

    return merged;
  };
  const handleAddFireId = useCallback((id) => {
    setSelectedFiresIds((prevIds) => {
      // Check if the ID is already in the list before adding
      if (!prevIds.includes(id)) {
        const newIds = [...prevIds, id]; // Add the ID to the list
        return newIds;
      }
      return prevIds; // Return the same list if the ID already exists
    });
  }, []);

  const handleRemoveFireId = useCallback((id) => {
    setSelectedFiresIds((prevIds) => {
      // Remove the ID from the list
      const newIds = prevIds.filter((prevId) => prevId !== id);
      return newIds;
    });
  }, []);
  //USER HISTORIC EVENTS
  const getUserHistoricEvents = async () => {
    try {
      const response = await physicModelApi.getAllUserHistoricEventsApi();
      if (response.status === 200) {
        const resultsArray = Object.values(response.data.data); // Convert object to array
        console.log("use historic:", resultsArray);
        setUserHistoricEventsList((prev) => ({
          ...prev,
          data: resultsArray,
        }));
      }
    } catch (error) {
      console.error("Error fdetching user Historic events data :", error);
    }
  };
  const getHistoricEventPolygons = async (id) => {
    try {
      const response = await physicModelApi.getHistoricEventByIdApi(id);
      if (response.status === 200) {
        const data = response.data.polygons;
        setUserHistoricEventsList((prev) => ({ ...prev, polygons: data }));
      }
    } catch (error) {
      console.error("Error fetching user historic events polygons:", error);
    } finally {
      setLoadingEventId(null); // clear regardless of success/failure
    }
  };
  // Function to handle event selection/deselection
  const handleEventSelect = (eventId) => {
    const isDeselecting = userHistoricEventsList.selectedEventId === eventId;

    if (isDeselecting) {
      setUserHistoricEventsList((prev) => ({
        ...prev,
        selectedEventId: 0,
        polygons: [],
      }));
      setLoadingEventId(null);
    } else {
      setUserHistoricEventsList((prev) => ({
        ...prev,
        selectedEventId: eventId,
        polygons: [],
      }));
      setLoadingEventId(eventId);
      getHistoricEventPolygons(eventId);
    }
  };
  //---------------------------------- draw tools functions-------------------------------
  const initializeDrawTools = (triggerTooltip) => {
    if (!mapRef.current) return;

    addDrawTools(
      mapRef,
      drawRef,
      editingRef,
      originalGeometryRef,
      markersRef,
      setCurrentPopupFeature,
      (errorMessage) => {
        triggerTooltip?.(errorMessage);
      },
      () => {},
      (format) => handleExportRef.current?.(format),
    );
  };
  useEffect(() => {
    if (!currentPopupFeature) {
      popupRef.current.remove();
      return;
    }

    const { id, lngLat, feature } = currentPopupFeature;
    const isAnyPredictionAvailable =
      isOperationRunning ||
      (geoJsonResults.length > 0 &&
        geoJsonResults.some((r) => r.contour !== null));

    let popupHTML = "";

    if (feature.geometry.type === "Point") {
      const pointCoordinates = feature.geometry.coordinates;
      const lat = pointCoordinates[1];
      const lon = pointCoordinates[0];

      popupHTML = `
  <table class="popup-table">
    <tr><th>Type</th><td>Point</td></tr>
    <tr><th>Latitude</th><td>${lat.toFixed(6)}</td></tr>
    <tr><th>Longitude</th><td>${lon.toFixed(6)}</td></tr>
  </table>
  <div class="popup-actions">
    <button class="delete-shape-button">Supprimer</button>
  </div>
`;
    } else if (feature.geometry.type === "Polygon") {
      const areaM2 = turf.area(feature);
      const areaKm2 = areaM2 / 1000000;
      const perimeter = turf.length(feature, { units: "kilometers" });

      let areaRow;
      if (areaKm2 < 0.01) {
        // Show area in square metres (integer or one decimal)
        areaRow = `<tr><th>Aire en m²</th><td>${areaM2.toFixed(0)}</td></tr>`;
      } else {
        areaRow = `<tr><th>Aire en km²</th><td>${areaKm2.toFixed(2)}</td></tr>`;
      }

      popupHTML = `
  <table class="popup-table">
    <tr><th>Type</th><td>Polygone</td></tr>
    ${areaRow}
    <tr><th>Aire en ha</th><td>${(areaM2 / 10000).toFixed(2)}</td></tr>
    <tr><th>Périmètre</th><td>${perimeter.toFixed(2)} km</td></tr>
  </table>
      <div class="popup-actions">
        <button class="edit-shape-button">Modifier</button>
        <button class="delete-shape-button delete">Supprimer</button>
      </div>`;
    } else if (feature.geometry.type === "LineString") {
      const length = turf.length(feature, { units: "kilometers" });

      popupHTML = `
      <table class="popup-table">
        <tr><th>Type</th><td>Ligne</td></tr>
        <tr><th>Longueur</th><td>${length.toFixed(2)} km</td></tr>
      </table>
      <div class="popup-actions">
        <button class="edit-shape-button">Modifier</button>
        <button class="delete-shape-button delete">Supprimer</button>
      </div>`;
    }

    popupRef.current.setLngLat(lngLat).setHTML(popupHTML).addTo(mapRef.current);

    // Use setTimeout to ensure DOM is rendered, then attach listeners
    setTimeout(() => {
      // --- DELETE BUTTON (works for Point, Polygon, LineString) ---
      const deleteButton = document.querySelector(".delete-shape-button");
      if (deleteButton) {
        deleteButton.addEventListener("click", () => {
          showDeleteModal(
            () => {
              // onConfirm
              if (feature.geometry.type === "Point") {
                const marker = markersRef.current.get(id);
                if (marker) marker.remove();
                markersRef.current.delete(id);
              }
              drawRef.current.delete(id);
              popupRef.current.remove();
              setCurrentPopupFeature(null);
            },
            () => {
              // onCancel — do nothing, modal closes itself
            },
          );
        });
      }

      // --- EDIT BUTTON (Polygon & LineString only) ---
      const editButton = document.querySelector(".edit-shape-button");
      if (editButton) {
        editButton.addEventListener("click", () => {
          popupRef.current.remove();
          editingRef.current = true;
          drawRef.current.changeMode("direct_select", { featureId: id });
          const selectedFeature = drawRef.current.get(id);
          if (selectedFeature) {
            originalGeometryRef.current = JSON.parse(
              JSON.stringify(selectedFeature),
            );
          }
        });
      }
    }, 100);
  }, [currentPopupFeature, geoJsonResults]);
  useEffect(() => {
    return () => {
      // Cleanup popup when component unmounts
      if (popupRef.current) {
        popupRef.current.remove();
      }
    };
  }, []);
  //---------------------------------- display layers functions-------------------------------
  //global function to display geojson layers
  const addGeoJsonLayer = (map, data, sourceId, layerConfig) => {
    if (!map.getSource(sourceId)) {
      map.addSource(sourceId, {
        type: "geojson",
        data: data,
      });
      map.addLayer(layerConfig);
    } else {
      // Update the source data if it exists
      map.getSource(sourceId).setData(data);
    }
  };
  const activeFiresShowRef = useRef(activeFires.show);
  useEffect(() => {
    activeFiresShowRef.current = activeFires.show;
  }, [activeFires.show]);

  useEffect(() => {
    if (!mapRef.current) return;
    const map = mapRef.current;
    const sourceId = "activeFires";
    const symbolLayerId = "activeFires-layer";

    const syncActiveFiresLayer = () => {
      // Style object not ready at all yet (very first mount) — bail, idle/next run will retry
      if (!map || !map.getStyle()) return;

      if (activeFires.show) {
        // Add or update the source
        if (!map.getSource(sourceId)) {
          map.addSource(sourceId, {
            type: "geojson",
            data: activeFiresDisplayData,
          });
        } else {
          map.getSource(sourceId).setData(activeFiresDisplayData);
        }

        const addSymbolLayer = () => {
          // Bail if the layer was hidden (or unmounted) while the icon was
          // still loading, or if the source got removed in the meantime.
          if (!activeFiresShowRef.current) return;
          if (!map.getSource(sourceId)) return;

          if (!map.getLayer(symbolLayerId)) {
            map.addLayer({
              id: symbolLayerId,
              type: "symbol",
              source: sourceId,
              layout: {
                "icon-image": "active-fire-icon",
                "icon-size": [
                  "interpolate",
                  ["linear"],
                  ["zoom"],
                  4,
                  0.12,
                  10,
                  0.2,
                  15,
                  0.3,
                ],
                "icon-allow-overlap": true,
                "icon-ignore-placement": true,
              },
            });

            map.on("click", symbolLayerId, (e) => {
              if (activeFirePopupRef.current) {
                activeFirePopupRef.current.remove();
                activeFirePopupRef.current = null;
              }

              const coordinates = e.lngLat;
              const properties = e.features[0].properties;
              const popupContent = `
        <div class="shared-popup">
          <div class="sp-row">
            <div class="sp-label">
              <span class="sp-dot" style="background: #b10511;"></span>
              <span class="sp-label-text">Date d'acquisition</span>
            </div>
            <div class="sp-value-wrap">
              <span class="sp-value">${formatDateValue(properties.acq_cest)}</span>
            </div>
          </div>

          <div class="sp-row">
            <div class="sp-label">
              <span class="sp-dot" style="background: #b10511;"></span>
              <span class="sp-label-text">Pays</span>
            </div>
            <div class="sp-value-wrap">
              <span class="sp-value">${properties.country !== undefined && properties.country !== null ? properties.country : "--"}</span>
            </div>
          </div>
        </div>
      `;

              const popup = new mapboxgl.Popup({
                className: "shared-popup",
                closeButton: true,
                closeOnClick: false,
                offset: 25,
              })
                .setLngLat(coordinates)
                .setHTML(popupContent)
                .addTo(map);

              activeFirePopupRef.current = popup;

              popup.on("close", () => {
                if (activeFirePopupRef.current === popup) {
                  activeFirePopupRef.current = null;
                }
              });
            });

            map.on("mouseenter", symbolLayerId, () => {
              map.getCanvas().style.cursor = "pointer";
            });
            map.on("mouseleave", symbolLayerId, () => {
              map.getCanvas().style.cursor = "";
            });
          }
        };

        // Load the icon once, then (re)try adding the layer.
        if (!map.hasImage("active-fire-icon")) {
          map.loadImage(`${assets.activeFire}`, (error, image) => {
            if (error) throw error;
            if (!map.hasImage("active-fire-icon")) {
              map.addImage("active-fire-icon", image);
            }
            addSymbolLayer();
          });
        } else {
          addSymbolLayer();
        }
      } else {
        // Remove the symbol layer and the source
        if (map.getLayer(symbolLayerId)) map.removeLayer(symbolLayerId);
        if (map.getSource(sourceId)) map.removeSource(sourceId);
        if (activeFirePopupRef.current) {
          activeFirePopupRef.current.remove();
          activeFirePopupRef.current = null;
        }
      }
    };

    // Try immediately — works even mid-zoom/mid-tile-load, since add/remove
    // calls only need the style object to exist, not to be fully "loaded".
    syncActiveFiresLayer();

    // Fallback safety net: `style.load` doesn't fire on zoom/tile activity,
    // so use `idle` instead — it reliably fires once the map settles,
    // ensuring we never permanently miss a show/hide toggle.
    map.on("idle", syncActiveFiresLayer);

    return () => {
      map.off("idle", syncActiveFiresLayer);
    };
  }, [activeFires.data, activeFires.show, activeFiresDisplayData]);
  //handle historic fires polygons
  useEffect(() => {
    if (mapRef.current) {
      const handleStyleLoad = () => {
        if (historicFirePopupRef.current) {
          historicFirePopupRef.current.remove();
          historicFirePopupRef.current = null;
        }
        if (
          filteredHistoricFiresPolygons &&
          filteredHistoricFiresPolygons.length > 0
        ) {
          // Merge multiple GeoJSONs into one (for the fill layer)
          const mergedGeoJSON = mergeGeoJSON(filteredHistoricFiresPolygons);

          // ---- Build ONE icon point per fire (not per polygon fragment) ----
          // Each entry in filteredHistoricFiresPolygons = one selected fire,
          // even if that fire itself is a MultiPolygon or many small features.
          const iconFeatures = filteredHistoricFiresPolygons.map(
            (fireGeoJSON) => {
              const center = turf.center(fireGeoJSON); // single representative point
              const representativeProps =
                fireGeoJSON.features && fireGeoJSON.features[0]
                  ? fireGeoJSON.features[0].properties
                  : fireGeoJSON.properties || {};
              center.properties = representativeProps;
              return center;
            },
          );
          const iconGeoJSON = {
            type: "FeatureCollection",
            features: iconFeatures,
          };

          // Always use the last polygon in the list for the flyTo
          const lastPolygon =
            filteredHistoricFiresPolygons[
              filteredHistoricFiresPolygons.length - 1
            ];

          const bbox = turf.bbox(lastPolygon);

          if (
            bbox[0] >= -180 &&
            bbox[0] <= 180 &&
            bbox[1] >= -90 &&
            bbox[1] <= 90 &&
            bbox[2] >= -180 &&
            bbox[2] <= 180 &&
            bbox[3] >= -90 &&
            bbox[3] <= 90
          ) {
            const bounds = new mapboxgl.LngLatBounds(
              [bbox[0], bbox[1]],
              [bbox[2], bbox[3]],
            );

            mapRef.current.fitBounds(bounds, {
              padding: 80, // px of breathing room around the polygon
              maxZoom: 15, // cap for very small / point-like fires
              duration: 1000,
            });
          } else {
            console.warn("Invalid bounding box for last polygon:", bbox);
          }

          const iconSourceId = "historicFires-icons";
          const symbolLayerId = "historicFires-symbol-layer";

          // ---- Fill layer / polygon source (unchanged logic) ----
          const source = mapRef.current.getSource("historicFires");
          if (source) {
            source.setData(mergedGeoJSON);
          } else {
            mapRef.current.addSource("historicFires", {
              type: "geojson",
              data: mergedGeoJSON,
            });

            mapRef.current.addLayer({
              id: "historicFires-layer",
              type: "fill",
              source: "historicFires",
              paint: {
                "fill-color": "red",
                "fill-opacity": 0.5,
              },
            });
          }

          // ---- Icon point source: exactly one point per fire ----
          const iconSource = mapRef.current.getSource(iconSourceId);
          if (iconSource) {
            iconSource.setData(iconGeoJSON);
          } else {
            mapRef.current.addSource(iconSourceId, {
              type: "geojson",
              data: iconGeoJSON,
            });

            mapRef.current.loadImage(
              `${assets.historicFire}`,
              (error, image) => {
                if (error) {
                  console.error("Error loading historic fire icon:", error);
                  return;
                }
                if (!mapRef.current.hasImage("historic-fire-icon")) {
                  mapRef.current.addImage("historic-fire-icon", image);
                }
                if (!mapRef.current.getLayer(symbolLayerId)) {
                  mapRef.current.addLayer({
                    id: symbolLayerId,
                    type: "symbol",
                    source: iconSourceId,
                    layout: {
                      "icon-image": "historic-fire-icon",
                      "icon-size": [
                        "interpolate",
                        ["linear"],
                        ["zoom"],
                        4,
                        0.07,
                        8,
                        0.07,
                        10,
                        0.0,
                      ],
                      "icon-allow-overlap": true,
                      "icon-ignore-placement": true,
                    },
                  });
                }
              },
            );
          }

          // ---- Popup + hover logic, attached once ----
          if (!source) {
            let currentPopup = null;

            const handleHistoricFireClick = (e) => {
              if (historicFirePopupRef.current) {
                historicFirePopupRef.current.remove();
              }

              const coordinates = e.lngLat;
              const properties = e.features[0].properties;
              const departmentLabel = fromBackendDepartmentCode(
                properties.department,
              );
              const formatAreaValue = (value) => {
                if (
                  value === undefined ||
                  value === null ||
                  value === "" ||
                  isNaN(value)
                ) {
                  return "--";
                }
                return parseFloat(value).toFixed(2);
              };

              const formatHectares = (value) => {
                if (
                  value === undefined ||
                  value === null ||
                  value === "" ||
                  isNaN(value)
                ) {
                  return "--";
                }
                return (parseFloat(value) / 10000).toFixed(2);
              };

              const areaM2 = formatAreaValue(properties.area_m2);
              const areaHa = formatHectares(properties.area_m2);

              const popupContent = `
              <div class="shared-popup">
                <div class="sp-row">
                  <div class="sp-label">
                    <span class="sp-dot" style="background: #b10511;"></span>
                    <span class="sp-label-text">Commune</span>
                  </div>
                  <div class="sp-value-wrap">
                    <span class="sp-value">${properties.commune !== undefined ? properties.commune : "--"}</span>
                  </div>
                </div>

                <div class="sp-row">
                  <div class="sp-label">
                    <span class="sp-dot" style="background: #b10511;"></span>
                    <span class="sp-label-text">Département</span>
                  </div>
                  <div class="sp-value-wrap">
                    <span class="sp-value">${departmentLabel !== undefined ? departmentLabel : "--"}</span>
                  </div>
                </div>
<div class="sp-row">
                  <div class="sp-label">
                    <span class="sp-dot" style="background: #b10511;"></span>
                    <span class="sp-label-text">Pays</span>
                  </div>
                  <div class="sp-value-wrap">
                   <span class="sp-value">${
                     properties.country !== undefined
                       ? properties.country.charAt(0).toUpperCase() +
                         properties.country.slice(1)
                       : "--"
                   }</span>
                  </div>
                </div>
                <div class="sp-row">
                  <div class="sp-label">
                    <span class="sp-dot" style="background: #b10511;"></span>
                    <span class="sp-label-text">Date d'alerte</span>
                  </div>
                  <div class="sp-value-wrap">
                    <span class="sp-value">${properties.alerte !== undefined ? formatDateValue(properties.alerte) : "--"}</span>
                  </div>
                </div>

                <div class="sp-row">
                  <div class="sp-label">
                    <span class="sp-dot" style="background: #b10511;"></span>
                    <span class="sp-label-text">Année</span>
                  </div>
                  <div class="sp-value-wrap">
                    <span class="sp-value">${properties.year !== undefined ? properties.year : "--"}</span>
                  </div>
                </div>

                <div class="sp-row">
                  <div class="sp-label">
                    <span class="sp-dot" style="background: #b10511;"></span>
                    <span class="sp-label-text">Date de fin</span>
                  </div>
                  <div class="sp-value-wrap">
                    <span class="sp-value">${properties.end_date !== undefined ? formatDateValue(properties.end_date) : "--"}</span>                  </div>
                </div>

                <div class="sp-row">
                  <div class="sp-label">
                    <span class="sp-dot" style="background: #b10511;"></span>
                    <span class="sp-label-text">Nature</span>
                  </div>
                  <div class="sp-value-wrap">
                    <span class="sp-value">${properties.nature !== undefined ? properties.nature : "--"}</span>
                  </div>
                </div>

                <div class="sp-row">
                  <div class="sp-label">
                    <span class="sp-dot" style="background: #b10511;"></span>
                    <span class="sp-label-text">Superficie</span>
                  </div>
                  <div class="sp-value-wrap">
                    <span class="sp-value">${areaM2}</span>
                    <span class="sp-unit">m²</span>
                  </div>
                </div>

                <div class="sp-row">
                  <div class="sp-label">
                    <span class="sp-dot" style="background: #b10511;"></span>
                    <span class="sp-label-text">Superficie</span>
                  </div>
                  <div class="sp-value-wrap">
                    <span class="sp-value">${areaHa}</span>
                    <span class="sp-unit">ha</span>
                  </div>
                </div>
              </div>
            `;

              const popup = new mapboxgl.Popup({
                className: "shared-popup",
                closeButton: true,
                closeOnClick: false,
                offset: 25,
              })
                .setLngLat(coordinates)
                .setHTML(popupContent)
                .addTo(mapRef.current);

              historicFirePopupRef.current = popup;

              popup.on("close", () => {
                if (historicFirePopupRef.current === popup) {
                  historicFirePopupRef.current = null;
                }
              });
            };

            const handlePointerEnter = () => {
              mapRef.current.getCanvas().style.cursor = "pointer";
            };
            const handlePointerLeave = () => {
              mapRef.current.getCanvas().style.cursor = "";
            };

            mapRef.current.on(
              "click",
              "historicFires-layer",
              handleHistoricFireClick,
            );
            mapRef.current.on(
              "mouseenter",
              "historicFires-layer",
              handlePointerEnter,
            );
            mapRef.current.on(
              "mouseleave",
              "historicFires-layer",
              handlePointerLeave,
            );

            mapRef.current.on("click", symbolLayerId, handleHistoricFireClick);
            mapRef.current.on("mouseenter", symbolLayerId, handlePointerEnter);
            mapRef.current.on("mouseleave", symbolLayerId, handlePointerLeave);
          }
        } else {
          // Remove layers and sources
          if (mapRef.current.getLayer("historicFires-symbol-layer")) {
            mapRef.current.removeLayer("historicFires-symbol-layer");
          }
          if (mapRef.current.getLayer("historicFires-layer")) {
            mapRef.current.removeLayer("historicFires-layer");
          }
          if (mapRef.current.getSource("historicFires-icons")) {
            mapRef.current.removeSource("historicFires-icons");
          }
          if (mapRef.current.getSource("historicFires")) {
            mapRef.current.removeSource("historicFires");
          }
        }
      };

      if (mapRef.current.isStyleLoaded()) {
        handleStyleLoad();
      }

      mapRef.current.on("style.load", handleStyleLoad);

      return () => {
        mapRef.current.off("style.load", handleStyleLoad);
      };
    }
  }, [
    filteredHistoricFiresPolygons,
    showHistoricFiresList,
    // showHistoricFiresByDateList,
  ]);

  useEffect(() => {
    if (mapRef.current) {
      const handleStyleLoad = () => {
        const predictionGeoJSONs = {};

        if (
          userHistoricEventsList.polygons &&
          userHistoricEventsList.polygons.length > 0
        ) {
          const polygons = userHistoricEventsList.polygons;
          const mainPolygons = polygons.filter((p) => p.type === "main");
          const predictionPolygons = polygons.filter(
            (p) => p.type === "predictions",
          );
          const startPoints = polygons.filter((p) => p.type === "start");

          const geoJSONMain = {
            type: "FeatureCollection",
            features: mainPolygons.flatMap((p) =>
              p.features.flatMap((fc) => fc.features),
            ),
          };

          predictionPolygons.forEach((p) => {
            const order = p.order;
            if (!predictionGeoJSONs[order]) {
              predictionGeoJSONs[order] = {
                type: "FeatureCollection",
                features: [],
              };
            }
            predictionGeoJSONs[order].features.push(
              ...p.features.flatMap((fc) => fc.features),
            );
          });

          const geoJSONStart = {
            type: "FeatureCollection",
            features: startPoints.flatMap((p) =>
              p.features.flatMap((fc) => fc.features),
            ),
          };

          // ── Helper: add or update a source + layer ──────────────────────────
          const addSourceAndLayer = (
            sourceId,
            geoJSON,
            layerId,
            type,
            color,
            dash = [],
            width = 3,
          ) => {
            if (geoJSON.features.length === 0) return;

            if (mapRef.current.getSource(sourceId)) {
              mapRef.current.getSource(sourceId).setData(geoJSON);
            } else {
              mapRef.current.addSource(sourceId, {
                type: "geojson",
                data: geoJSON,
              });

              if (type === "line") {
                const paint = {
                  "line-color": color,
                  "line-width": width,
                };
                if (dash && dash.length > 0) {
                  paint["line-dasharray"] = dash;
                }
                mapRef.current.addLayer({
                  id: layerId,
                  type: "line",
                  source: sourceId,
                  paint,
                });
              } else if (type === "symbol") {
                mapRef.current.addLayer({
                  id: layerId,
                  type: "symbol",
                  source: sourceId,
                  layout: {
                    "icon-image": "start-point-icon",
                    "icon-size": 0.018,
                    "icon-allow-overlap": true,
                    "icon-ignore-placement": true,
                  },
                });
              }
            }
          };

          // ── Main polygon: solid red line ─────────────────────────────────────
          addSourceAndLayer(
            "mainPolygons",
            geoJSONMain,
            "mainPolygons-layer",
            "line",
            "red",
            [],
            3,
          );

          // ── Prediction polygons: solid for hour boundaries, dashed for intermediates ───────────
          // Resolve the time step used when this historic event was created
          const selectedEvent = userHistoricEventsList.data.find(
            (e) => e.fire?.id === userHistoricEventsList.selectedEventId,
          );
          const dtPolygons = selectedEvent?.fire?.dt_polygons || 60;
          const dtToTimeStep = {
            60: "1h",
            30: "30min",
            45: "45min",
            15: "15min",
          };
          const historicTimeStep = dtToTimeStep[dtPolygons] || "1h";

          // Sort orders numerically so colors are assigned in temporal order
          const sortedOrders = Object.keys(predictionGeoJSONs).sort(
            (a, b) => Number(a) - Number(b),
          );

          sortedOrders.forEach((order, index) => {
            const totalOrders = sortedOrders.length;
            const color = interpolateYellowToRed(
              totalOrders === 1 ? 1 : index / (totalOrders - 1),
            );
            const hourBoundary = isHourBoundary(
              parseInt(order, 10) - 1,
              historicTimeStep,
            );
            addSourceAndLayer(
              `predictionPolygons-${order}`,
              predictionGeoJSONs[order],
              `predictionPolygons-layer-${order}`,
              "line",
              color,
              hourBoundary ? [1] : [3, 3],
              hourBoundary ? 3 : 1.5,
            );
          });

          // ── Start point icon ─────────────────────────────────────────────────
          mapRef.current.loadImage("/red_star.png", (error, image) => {
            if (error) {
              console.error("Error loading icon:", error);
              return;
            }
            if (!mapRef.current.hasImage("start-point-icon")) {
              mapRef.current.addImage("start-point-icon", image);
            }

            addSourceAndLayer(
              "startPoint",
              geoJSONStart,
              "startPoint-layer",
              "symbol",
              "black",
            );

            if (geoJSONMain.features.length > 0) {
              const lastFeature =
                geoJSONMain.features[geoJSONMain.features.length - 1];
              const bbox = turf.bbox(lastFeature);

              if (
                bbox[0] >= -180 &&
                bbox[0] <= 180 &&
                bbox[1] >= -90 &&
                bbox[1] <= 90 &&
                bbox[2] >= -180 &&
                bbox[2] <= 180 &&
                bbox[3] >= -90 &&
                bbox[3] <= 90
              ) {
                const center = turf.center(lastFeature);
                mapRef.current.flyTo({
                  center: center.geometry.coordinates,
                  zoom: 11,
                  duration: 2000,
                  essential: true,
                });
              } else {
                console.warn("Invalid bounding box for feature:", bbox);
              }
            }
          });
        } else {
          // ── Cleanup: remove all layers/sources dynamically ───────────────────

          if (mapRef.current.getLayer("mainPolygons-layer"))
            mapRef.current.removeLayer("mainPolygons-layer");
          if (mapRef.current.getSource("mainPolygons"))
            mapRef.current.removeSource("mainPolygons");

          if (mapRef.current.getLayer("startPoint-layer"))
            mapRef.current.removeLayer("startPoint-layer");
          if (mapRef.current.getSource("startPoint"))
            mapRef.current.removeSource("startPoint");

          // Remove all prediction layers dynamically
          const style = mapRef.current.getStyle();
          if (style && style.layers) {
            style.layers
              .filter((l) => l.id.startsWith("predictionPolygons-layer-"))
              .forEach((l) => {
                if (mapRef.current.getLayer(l.id))
                  mapRef.current.removeLayer(l.id);
              });
          }
          if (style && style.sources) {
            Object.keys(style.sources)
              .filter((s) => s.startsWith("predictionPolygons-"))
              .forEach((s) => {
                if (mapRef.current.getSource(s)) mapRef.current.removeSource(s);
              });
          }
        }
      };

      if (mapRef.current.isStyleLoaded()) {
        handleStyleLoad();
      } else {
        mapRef.current.on("style.load", handleStyleLoad);
      }

      return () => {
        mapRef.current.off("style.load", handleStyleLoad);
      };
    }
  }, [
    userHistoricEventsList.polygons,
    userHistoricEventsList.selectedEventId,
    userHistoricEventsList.data,
  ]);
  useRasterTile(mapRef, "dem", opacities["demLevel"] / 100, demLevel.show, {
    priorityLayers: contourLayers,
    country: vegetationCountry,
    department: selectedVegDepartment,
  });
  useRasterTile(
    mapRef,
    "lai",
    opacities["leafAreaIndex"] / 100,
    leafAreaIndex.show,
    {
      priorityLayers: contourLayers,
      country: vegetationCountry,
      department: selectedVegDepartment,
    },
  );
  useRasterTile(mapRef, "urban", opacities["urban"] / 100, urban.show, {
    priorityLayers: contourLayers,
    country: vegetationCountry,
    department: selectedVegDepartment,
  });
  useRasterTile(
    mapRef,
    "vegetationHeight",
    opacities["vegetationHeight"] / 100,
    vegetationHeight.show,
    {
      priorityLayers: contourLayers,
      country: vegetationCountry,
      department: selectedVegDepartment,
    },
  );
  useRasterTile(mapRef, "biomass", opacities["biomass"] / 100, biomass.show, {
    priorityLayers: contourLayers,
    country: vegetationCountry,
    department: selectedVegDepartment,
  });
  useRasterTile(
    mapRef,
    "totalFuelLoad",
    opacities["totalFuelLoad"] / 100,
    totalFuelLoad.show,
    {
      priorityLayers: contourLayers,
      country: vegetationCountry,
      department: selectedVegDepartment,
    },
  );
  useRasterTile(
    mapRef,
    "bareSoil",
    opacities["bareSoil"] / 100,
    baresoil.show,
    {
      priorityLayers: contourLayers,
      country: vegetationCountry,
      department: selectedVegDepartment,
    },
  );
  useRasterTile(
    mapRef,
    "coniferous",
    opacities["coniferous"] / 100,
    coniferous.show,
    {
      priorityLayers: contourLayers,
      country: vegetationCountry,
      department: selectedVegDepartment,
    },
  );
  useRasterTile(
    mapRef,
    "shrubland",
    opacities["shrubland"] / 100,
    shrubland.show,
    {
      priorityLayers: contourLayers,
      country: vegetationCountry,
      department: selectedVegDepartment,
    },
  );
  useRasterTile(
    mapRef,
    "grassland",
    opacities["grassland"] / 100,
    grassland.show,
    {
      priorityLayers: contourLayers,
      country: vegetationCountry,
      department: selectedVegDepartment,
    },
  );
  useRasterTile(
    mapRef,
    "cropland",
    opacities["cropland"] / 100,
    cropland.show,
    {
      priorityLayers: contourLayers,
      country: vegetationCountry,
      department: selectedVegDepartment,
    },
  );
  useRasterTile(
    mapRef,
    "broadleaf",
    opacities["broadleaf"] / 100,
    broadleaf.show,
    {
      priorityLayers: contourLayers,
      country: vegetationCountry,
      department: selectedVegDepartment,
    },
  );
  useRasterTile(mapRef, "pine", opacities["pine"] / 100, pine.show, {
    priorityLayers: contourLayers,
    country: vegetationCountry,
    department: selectedVegDepartment,
  });
  useRasterTile(mapRef, "oak", opacities["oak"] / 100, oak.show, {
    priorityLayers: contourLayers,
    country: vegetationCountry,
    department: selectedVegDepartment,
  });
  useRasterTile(
    mapRef,
    "mixedForest",
    opacities["mixedForest"] / 100,
    mixedForest.show,
    {
      priorityLayers: contourLayers,
      country: vegetationCountry,
      department: selectedVegDepartment,
    },
  );
  useRasterTile(
    mapRef,
    "deadFuelMoisture",
    opacities["deadFuel"] / 100,
    deadFuel.show,
    {
      priorityLayers: contourLayers,
      country: vegetationCountry,
      date: dateString,
      time: timeString,
      utc: currentTimeZone,
      department: selectedVegDepartment,
    },
  );
  useRasterTile(
    mapRef,
    "soilMoisture",
    opacities["Soil"] / 100,
    soilMoisture.show,
    {
      priorityLayers: contourLayers,
      country: weatherCountry,
      department: selectedWeatherDepartment,
    },
  );
  useRasterTile(
    mapRef,
    "windGust",
    opacities["windGust"] / 100,
    windGust.show,
    {
      priorityLayers: contourLayers,
      country: weatherCountry,
      date: dateString,
      time: timeString,
      utc: currentTimeZone,
      department: selectedWeatherDepartment,
    },
  );
  useRasterTile(mapRef, "windDirection", 1, windDirectionData.show, {
    priorityLayers: contourLayers,
    country: weatherCountry,
    date: dateString,
    time: timeString,
    utc: currentTimeZone,
    department: selectedWeatherDepartment,
  });

  useRasterTile(
    mapRef,
    "windSpeed",
    opacities["windSpeed"] / 100,
    windSpeedData.show,
    {
      priorityLayers: contourLayers,
      country: weatherCountry,
      date: dateString,
      time: timeString,
      utc: currentTimeZone,
      department: selectedWeatherDepartment,
    },
  );
  useRasterTile(
    mapRef,
    "precipitations",
    opacities["precipitations"] / 100,
    precipitations.show,
    {
      priorityLayers: contourLayers,
      country: weatherCountry,
      date: dateString,
      time: timeString,
      utc: currentTimeZone,
      department: selectedWeatherDepartment,
    },
  );

  useRasterTile(
    mapRef,
    "hourlyPrecipitations",
    opacities["hourlyPrecipitations"] / 100,
    hourlyPrecipitations.show,
    {
      priorityLayers: contourLayers,
      country: weatherCountry,
      date: dateString,
      time: timeString,
      utc: currentTimeZone,
      department: selectedWeatherDepartment,
    },
  );
  useRasterTile(
    mapRef,
    "temperature",
    opacities["temperature"] / 100,
    temperature.show,
    {
      priorityLayers: contourLayers,
      country: weatherCountry,
      date: dateString,
      time: timeString,
      utc: currentTimeZone,
      department: selectedWeatherDepartment,
    },
  );
  useRasterTile(
    mapRef,
    "relativeHumidity",
    opacities["relativeHumidity"] / 100,

    relativeHumidity.show,
    {
      priorityLayers: contourLayers,
      country: weatherCountry,
      date: dateString,
      time: timeString,
      utc: currentTimeZone,
      department: selectedWeatherDepartment,
    },
  );
  useRasterTile(
    mapRef,
    "meanSeaLevelPressure",
    opacities["meanSeaLevelPressure"] / 100,

    meanSeaLevelPressure.show,
    {
      priorityLayers: contourLayers,
      country: weatherCountry,
      date: dateString,
      time: timeString,
      utc: currentTimeZone,
      department: selectedWeatherDepartment,
    },
  );
  useRasterTile(
    mapRef,
    "cloudCover",
    opacities["cloudCover"] / 100,

    cloudCover.show,
    {
      priorityLayers: contourLayers,
      country: weatherCountry,
      date: dateString,
      time: timeString,
      utc: currentTimeZone,
      department: selectedWeatherDepartment,
    },
  );

  useVectorTile(mapRef, "regions", 1, regions.show, {
    priorityLayers: contourLayers,
    enablePopup: true,
  });

  useVectorTile(mapRef, "departments", 1, departements.show, {
    priorityLayers: contourLayers,
    enablePopup: true,
  });
  useVectorTile(mapRef, "majorRoads", 1, majorRoads.show, {
    priorityLayers: contourLayers,
    country: vegetationCountry,
    department: selectedVegDepartment,
    enablePopup: true,
  });

  useVectorTile(mapRef, "secondaryRoads", 1, majorRoads.show, {
    priorityLayers: contourLayers,
    country: vegetationCountry,
    department: selectedVegDepartment,
    enablePopup: true,
  });

  useVectorTile(mapRef, "residentialRoads", 1, majorRoads.show, {
    priorityLayers: contourLayers,
    country: vegetationCountry,
    department: selectedVegDepartment,
    enablePopup: true,
  });

  useVectorTile(mapRef, "paths", 1, majorRoads.show, {
    priorityLayers: contourLayers,
    country: vegetationCountry,
    department: selectedVegDepartment,
    enablePopup: true,
  });
  useVectorTile(mapRef, "railways", 1, railways.show, {
    priorityLayers: contourLayers,
    country: vegetationCountry,
    department: selectedVegDepartment,
    enablePopup: true,
  });
  useVectorTile(mapRef, "trainStations", 1, railways.show, {
    priorityLayers: contourLayers,
    country: vegetationCountry,
    department: selectedVegDepartment,
    enablePopup: true,
  });
  useVectorTile(mapRef, "fireStations", 1, fireStationsData.show, {
    priorityLayers: contourLayers,
    country: vegetationCountry,
    department: selectedVegDepartment,
    enablePopup: true,
  });
  useVectorTile(mapRef, "transformers", 1, electricalStations.show, {
    priorityLayers: contourLayers,
    country: vegetationCountry,
    department: selectedVegDepartment,
    enablePopup: true,
  });
  useVectorTile(mapRef, "gazLines", 1, gazLines.show, {
    priorityLayers: contourLayers,
    country: vegetationCountry,
    department: selectedVegDepartment,
    enablePopup: true,
  });
  useVectorTile(mapRef, "water", 1, water.show, {
    priorityLayers: contourLayers,
    country: vegetationCountry,
    department: selectedVegDepartment,
    enablePopup: true,
  });
  useVectorTile(mapRef, "pools", 1, artificielWater.show, {
    priorityLayers: contourLayers,
    country: vegetationCountry,
    department: selectedVegDepartment,
    enablePopup: true,
  });
  useVectorTile(mapRef, "gazRefuelingStations", 1, gazRefuelingStations.show, {
    priorityLayers: contourLayers,
    country: vegetationCountry,
    department: selectedVegDepartment,
    enablePopup: true,
  });
  useVectorTile(mapRef, "electricalLines", 1, electricalLines.show, {
    priorityLayers: contourLayers,
    country: vegetationCountry,
    department: selectedVegDepartment,
    enablePopup: true,
  });
  // useVectorTile(mapRef, "undergroundElectricalLines", 1, electricalLines.show, {
  //   priorityLayers: contourLayers,
  //   country: vegetationCountry,
  //   enablePopup: true,
  // });
  // useVectorTile(mapRef, "aerianElectricalLines", 1, electricalLines.show, {
  //   priorityLayers: contourLayers,
  //   country: vegetationCountry,
  //   enablePopup: true,
  // });
  // useVectorTile(mapRef, "crossingPoints", 1, electricalLines.show, {
  //   priorityLayers: contourLayers,
  //   country: vegetationCountry,
  //   enablePopup: true,
  // });
  // useVectorTile(
  //   mapRef,
  //   "electricalStationsPoints",
  //   1,
  //   electricalStations.show,
  //   {
  //     priorityLayers: contourLayers,
  //     country: vegetationCountry,
  //     enablePopup: true,
  //   },
  // );
  // useVectorTile(
  //   mapRef,
  //   "electricalStationsPolygons",
  //   1,
  //   electricalStations.show,
  //   {
  //     priorityLayers: contourLayers,
  //     country: vegetationCountry,
  //     enablePopup: true,
  //   },
  // );
  useVectorTile(
    mapRef,
    "electricalStationsPoints",
    1,
    electricalStations.show,
    {
      priorityLayers: contourLayers,
      country: vegetationCountry,
      department: selectedVegDepartment,
      enablePopup: true,
    },
  );
  // Handle DFCI layer and source
  useEffect(() => {
    if (mapRef.current) {
      if (dfci.data && dfci.show) {
        addGeoJsonLayer(mapRef.current, dfci.data, "dfci", {
          id: "dfci-layer",
          type: "line",
          source: "dfci",
          paint: {
            "line-color": "#0B03FF",
            "line-width": 2,
            "line-dasharray": [2, 2], // [dash length, gap length]
          },
        });
        // Add a symbol layer to display the names
        if (!mapRef.current.getLayer("dfci-names")) {
          mapRef.current.addLayer({
            id: "dfci-names",
            type: "symbol",
            source: "dfci",
            layout: {
              "text-field": ["get", "NOM"], // Updated to use 'NOM'
              "text-font": ["Arial Unicode MS Bold"],
              "text-size": 12,
              "text-offset": [0, 0.5],
              "text-anchor": "top",
            },
            paint: {
              "text-color": "#0B03FF", // Set text color to blue
              "text-halo-color": "white", // Set halo color (border) to white
              "text-halo-width": 1.5, // Set the width of the halo
            },
          });
        }
      } else {
        // Remove both the dfci layer and dfci names layer when showDfci is unchecked
        if (mapRef.current.getLayer("dfci-layer")) {
          mapRef.current.removeLayer("dfci-layer");
        }
        if (mapRef.current.getLayer("dfci-names")) {
          mapRef.current.removeLayer("dfci-names");
        }
        if (mapRef.current.getSource("dfci")) {
          mapRef.current.removeSource("dfci");
        }
      }
    }
  }, [dfci.data, dfci.show]);
  //------------------------------------MODEL RELATED LAYERS DISPLAY------------------------------------
  //Handle DETECTED BUILDINGS IN RISK layer and source
  useEffect(() => {
    if (!mapRef.current) return;
    const map = mapRef.current;

    const handleStyleLoad = () => {
      // Pre-load the shared icon once
      const loadIconAndAddLayers = () => {
        geoJsonResults.forEach((result, idx) => {
          const n = idx + 1;
          const sourceId = `highlighted-buildings${n}`;
          const fillLayerId = `highlighted-buildings${n}-layer`;
          const symbolLayerId = `highlighted-buildings${n}-symbol-layer`;

          if (result.building && result.showBuildings) {
            // Add/update source
            if (!map.getSource(sourceId)) {
              map.addSource(sourceId, {
                type: "geojson",
                data: result.building,
              });
            } else {
              map.getSource(sourceId).setData(result.building);
            }

            // Add fill layer
            if (!map.getLayer(fillLayerId)) {
              map.addLayer({
                id: fillLayerId,
                type: "fill",
                source: sourceId,
                paint: { "fill-color": "#8a8686" },
              });
            }

            // Add symbol layer — use the shared icon
            if (!map.hasImage("buildings-shared-icon")) {
              // Icon not loaded yet, load it then add the symbol layer
              map.loadImage(assets.buildings_t1, (error, image) => {
                if (error) return;
                if (!map.hasImage("buildings-shared-icon")) {
                  map.addImage("buildings-shared-icon", image);
                }
                if (!map.getLayer(symbolLayerId)) {
                  map.addLayer({
                    id: symbolLayerId,
                    type: "symbol",
                    source: sourceId,
                    layout: {
                      "icon-image": "buildings-shared-icon",
                      "icon-size": [
                        "interpolate",
                        ["linear"],
                        ["zoom"],
                        5,
                        0.02,
                        10,
                        0.03,
                        15,
                        0.03,
                      ],
                      "icon-allow-overlap": true,
                    },
                  });
                }
              });
            } else {
              // Icon already loaded, just add the layer
              if (!map.getLayer(symbolLayerId)) {
                map.addLayer({
                  id: symbolLayerId,
                  type: "symbol",
                  source: sourceId,
                  layout: {
                    "icon-image": "buildings-shared-icon",
                    "icon-size": [
                      "interpolate",
                      ["linear"],
                      ["zoom"],
                      5,
                      0.02,
                      10,
                      0.03,
                      15,
                      0.03,
                    ],
                    "icon-allow-overlap": true,
                  },
                });
              }
            }
          } else {
            // Remove layers if hidden or no data
            if (map.getLayer(fillLayerId)) map.removeLayer(fillLayerId);
            if (map.getLayer(symbolLayerId)) map.removeLayer(symbolLayerId);
            if (map.getSource(sourceId)) map.removeSource(sourceId);
          }
        });
      };

      loadIconAndAddLayers();
    };

    if (map.isStyleLoaded()) {
      handleStyleLoad();
    } else {
      map.on("style.load", handleStyleLoad);
    }

    return () => {
      map.off("style.load", handleStyleLoad);
    };
  }, [geoJsonResults]);
  const rebuildAllContourLayers = () => {
    if (!mapRef.current) return;
    if (geoJsonResults.length === 0) return;

    const map = mapRef.current;

    geoJsonResults.forEach((result, idx) => {
      if (!result?.contour) return;
      const n = idx + 1;
      const sourceId = `result${n}-source`;
      const fillLayerId = `result${n}-layer-fill`;
      const lineLayerId = `result${n}-layer`;
      const color = predictionColors[idx] || "#cc0000";
      const hourBoundary = isHourBoundary(idx, timeStep);

      if (map.getLayer(fillLayerId)) map.removeLayer(fillLayerId);
      if (map.getLayer(lineLayerId)) map.removeLayer(lineLayerId);
      if (map.getSource(sourceId)) map.removeSource(sourceId);

      map.addSource(sourceId, { type: "geojson", data: result.contour });
      const opacityKey = `geoJsonResult${n}`;
      const targetOpacity = (opacities[opacityKey] ?? 100) / 100;

      //respect showContour state
      const visibility = result.showContour !== false ? "visible" : "none";

      map.addLayer({
        id: fillLayerId,
        type: "fill",
        source: sourceId,
        paint: { "fill-color": color, "fill-opacity": targetOpacity },
        layout: { visibility },
      });
      map.addLayer({
        id: lineLayerId,
        type: "line",
        source: sourceId,
        paint: {
          "line-color": color,
          "line-width": hourBoundary ? 3 : 1.5,
          "line-dasharray": hourBoundary ? [1] : [3, 3],
          "line-opacity": 1,
        },
        layout: { visibility },
      });
    });
    enforceLayerOrder(map);
  };
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && mapRef.current) {
        // Seulement si des contours existent
        if (geoJsonResults.length > 0) {
          mapRef.current.resize(); // facultatif
          setTimeout(() => rebuildAllContourLayers(), 100);
        }
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [geoJsonResults, opacities]);
  useEffect(() => {
    if (!mapRef.current) return;
    const map = mapRef.current;

    // Helper to add a GeoJSON layer with animation
    const addGeoJsonLayerWithOpacity = (
      geoJsonData,
      sourceId,
      layerId,
      lineColor,
      fillColor,
      targetOpacityPercent,
      hourBoundary = false,
    ) => {
      if (!map.getSource(sourceId)) {
        map.addSource(sourceId, { type: "geojson", data: geoJsonData });
      } else {
        map.getSource(sourceId).setData(geoJsonData);
      }

      const fillLayerId = `${layerId}-fill`;
      const isNewFill = !map.getLayer(fillLayerId);
      if (isNewFill) {
        map.addLayer({
          id: fillLayerId,
          type: "fill",
          source: sourceId,
          paint: {
            "fill-color": fillColor,
            "fill-opacity": 0,
          },
        });
      }

      const isNewLine = !map.getLayer(layerId);
      if (isNewLine) {
        map.addLayer({
          id: layerId,
          type: "line",
          source: sourceId,
          paint: {
            "line-color": lineColor,
            //  Hour boundaries: thicker solid line
            //  Intermediate: thinner dashed line
            "line-width": hourBoundary ? 3 : 1.5,
            "line-dasharray": hourBoundary ? [1] : [3, 3],
            "line-opacity": 0,
          },
        });
        setAddedLayers((prev) => [...prev, { layerId, sourceId }]);
      }

      if (isNewFill || isNewLine) {
        map.once("idle", () => {
          enforceLayerOrder(map);
          const targetFill = targetOpacityPercent / 100;
          animateLayerOpacity(map, [fillLayerId], targetFill, 800);
          animateLayerOpacity(map, [layerId], 1, 800);
        });
      }
    };

    const displayPredictedContours = () => {
      // Add each predicted contour (reverse order so first is on top)
      [...geoJsonResults].reverse().forEach((result, reversedIdx) => {
        const originalIdx = geoJsonResults.length - 1 - reversedIdx;
        const n = originalIdx + 1;
        if (result?.contour) {
          const color = predictionColors[originalIdx] || "#cc0000";
          const sourceId = `result${n}-source`;
          const layerId = `result${n}-layer`;
          const opacityKey = `geoJsonResult${n}`;
          const targetOpacity = opacities[opacityKey] ?? 100;
          const hourBoundary = isHourBoundary(originalIdx, timeStep);

          // Pass boundary info to the layer adder
          addGeoJsonLayerWithOpacity(
            result.contour,
            sourceId,
            layerId,
            color,
            color,
            targetOpacity,
            hourBoundary,
          );
        }
      });

      // Input contour
      // if (geoJsonInputContour.contour) {
      //   if (!map.getSource("input-source")) {
      //     map.addSource("input-source", {
      //       type: "geojson",
      //       data: geoJsonInputContour.contour,
      //     });
      //   } else {
      //     map.getSource("input-source").setData(geoJsonInputContour.contour);
      //   }
      //   if (!map.getLayer("input-layer")) {
      //     map.addLayer({
      //       id: "input-layer",
      //       type: "line",
      //       source: "input-source",
      //       paint: { "line-color": "red", "line-width": 4 },
      //     });
      //   }
      // }

      map.once("idle", () => enforceLayerOrder(map));
    };

    if (map.isStyleLoaded()) {
      displayPredictedContours();
    } else {
      map.on("style.load", displayPredictedContours);
    }

    return () => {
      map.off("style.load", displayPredictedContours);
    };
  }, [geoJsonResults, geoJsonInputContour.contour, opacities]);
  //------------------------------- SHOW/HIDE DISPLAYED LAYERS ON MAP----------------------------------
  const setLoadingUserEvents = (isLoading) => {
    setUserHistoricEventsList((prev) => ({ ...prev, isLoading: isLoading }));
  };

  const toggleLayer = (setter, event, stateKey) => {
    setter((prev) => {
      const nextShow = !prev.show;

      const rasterLayerId = RASTER_LAYER_IDS[stateKey];
      if (rasterLayerId) {
        if (nextShow) {
          setRasterLayerOrder((prev) => [
            ...prev.filter((id) => id !== rasterLayerId),
            rasterLayerId,
          ]);
          if (mapRef.current) {
            mapRef.current.once("idle", () => {
              enforceRasterOrder(mapRef.current);
            });
          }
        } else {
          setRasterLayerOrder((prev) =>
            prev.filter((id) => id !== rasterLayerId),
          );
        }
      }
      return { ...prev, show: nextShow };
    });

    // ✅ Only open the legend if we actually have a mouse event
    if (!showLegendComponent && !isSmallScreen && event) {
      handleLegendComponentToggle(event);
    }
  };
  const onShowDepartements = (event) => toggleLayer(setDepartements, event);
  const onShowRegions = (event) => toggleLayer(setRegions, event);
  const onShowDfci = () => {
    setDfci((prev) => ({
      ...prev,
      show: !prev.show,
    }));
  };
  const onShowActiveFires = (event) => toggleLayer(setActiveFires, event);

  const onToggleActiveFiresPanel = () => {
    setShowActiveFiresPanel((prev) => !prev);
  };

  const onApplyActiveFiresFilters = (overrides = {}) => {
    const {
      startDate = activeFiresStartDate,
      endDate = activeFiresEndDate,
      startTime = activeFiresStartTime,
      endTime = activeFiresEndTime,
    } = overrides;
    getActiveFires(startDate, endDate, startTime, endTime);
  };
  const onShowWindDirection = () => {
    setWindDirectionData((prev) => ({
      ...prev,
      show: !prev.show,
    }));
  };
  const onShowSoilMoisture = (e) =>
    toggleLayer(setSoilMoisture, e, "soilMoisture");
  const onShowWindSpeedData = (e) =>
    toggleLayer(setWindSpeedData, e, "windSpeedData");
  const onShowBareSoil = (e) => toggleLayer(setBaresoil, e, "baresoil");

  const onShowBroadleaf = (e) => toggleLayer(setBroadleaf, e, "broadleaf");
  const onShowPine = (e) => toggleLayer(setPine, e, "pine");
  const onShowOak = (e) => toggleLayer(setOak, e, "oak");
  const onShowMixedForest = (e) =>
    toggleLayer(setMixedForest, e, "mixedForest");
  const onShowWater = (event) => toggleLayer(setWater, event);
  const onShowDemLevel = (e) => toggleLayer(setDemLevel, e, "demLevel");
  const onShowLai = (e) => toggleLayer(setLeafAreaIndex, e, "leafAreaIndex");
  const onShowVegetationHeight = (e) =>
    toggleLayer(setVegetationHeight, e, "vegetationHeight");
  const onShowBiomass = (e) => toggleLayer(setBiomass, e, "biomass");
  const onShowTotalFuelLoad = (e) =>
    toggleLayer(setTotalFuelLoad, e, "totalFuelLoad");
  const onShowArtificielWater = (event) =>
    toggleLayer(setArtificielWater, event);
  const onShowUrban = (event) => toggleLayer(setUrban, event);
  const onShowConiferous = (e) => toggleLayer(setConiferous, e, "coniferous");
  const onShowShrubland = (e) => toggleLayer(setShrubland, e, "shrubland");
  const onShowCropland = (e) => toggleLayer(setCropland, e, "cropland");
  const onShowGrassland = (e) => toggleLayer(setGrassland, e, "grassland");
  const onShowRailways = (event) => toggleLayer(setRailways, event);
  const onShowMajorRoads = (event) => toggleLayer(setMajorRoads, event);
  const onShowElectricalLines = (event) =>
    toggleLayer(setElectricalLines, event);
  const onShowElectricalStations = (event) =>
    toggleLayer(setElectricalStations, event);
  const onShowHistoricFiresList = () => {
    setShowHistoricFiresList(true);
    setShowHistoricFiresByDateList(false);
    setHistoricFiresStartDate("");
    setHistoricFiresEndDate(defaultHistoricEndDate);
  };

  const onShowHistoricFiresByDateList = () => {
    setShowHistoricFiresByDateList(true);
    setShowHistoricFiresList(false);
  };
  const onShowUserHistoricEvents = () => {
    setUserHistoricEventsList((prev) => ({
      ...prev,
      show: !prev.show,
    }));
  };
  const onShowGazLines = (event) => toggleLayer(setGazLines, event);
  const onShowGazRefuelingStations = (event) =>
    toggleLayer(setGazRefuelingStations, event);
  const onShowFireStations = (event) => toggleLayer(setFireStationsData, event);
  const onShowTrainStations = (event) =>
    toggleLayer(setTrainStationsData, event);
  const onShowPrecipitations = (e) =>
    toggleLayer(setPrecipitations, e, "precipitations");
  const onShowHourlyPrecipitations = (e) =>
    toggleLayer(setHourlyPrecipitations, e, "hourlyPrecipitations");
  const onShowDeadFuel = (e) => toggleLayer(setDeadFuel, e, "deadFuel");
  const onShowTemperature = (e) =>
    toggleLayer(setTemperature, e, "temperature");
  const onShowRelativeHumidity = (e) =>
    toggleLayer(setRelativeHumidity, e, "relativeHumidity");
  const onShowMeanSeaLevelPressure = (e) =>
    toggleLayer(setMeanSeaLevelPressure, e, "meanSeaLevelPressure");
  const onShowWindGust = (e) => toggleLayer(setWindGust, e, "windGust");
  const onShowCloudCover = (e) => toggleLayer(setCloudCover, e, "cloudCover");

  const toggleContourBuildingsVisibility = (contourNumber) => {
    setGeoJsonResults((prev) => {
      const updated = [...prev];
      const index = contourNumber - 1;
      if (updated[index]) {
        updated[index] = {
          ...updated[index],
          showBuildings: !updated[index].showBuildings,
        };
      }
      return updated;
    });
  };
  const toggleContourVisibility = (contourNumber) => {
    setGeoJsonResults((prev) => {
      const updated = [...prev];
      const index = contourNumber - 1;
      if (updated[index]) {
        updated[index] = {
          ...updated[index],
          showContour: !updated[index].showContour,
        };
        // Sync map layer visibility immediately
        if (mapRef.current) {
          const visibility = updated[index].showContour ? "visible" : "none";
          const fillId = `result${contourNumber}-layer-fill`;
          const lineId = `result${contourNumber}-layer`;
          if (mapRef.current.getLayer(fillId))
            mapRef.current.setLayoutProperty(fillId, "visibility", visibility);
          if (mapRef.current.getLayer(lineId))
            mapRef.current.setLayoutProperty(lineId, "visibility", visibility);
        }
      }
      return updated;
    });
  };
  useEffect(() => {
    return () => {
      if (mapRef.current) {
        removeDrawTools(mapRef, drawRef, markersRef);
      }
    };
  }, []);

  return (
    <MapTemplate
      showLayersComponent={showLayersComponent}
      handleDraggableComponentToggle={handleDraggableComponentToggle}
      mapContainerRef={mapContainerRef}
      mapRef={mapRef}
      is3DTerrain={is3DTerrain}
      is3DBuildings={is3DBuildings}
      setIs3DTerrain={setIs3DTerrain}
      setIs3DBuildings={setIs3DBuildings}
      regions={regions}
      onShowRegions={onShowRegions}
      departements={departements}
      onShowDepartements={onShowDepartements}
      opacities={opacities}
      onHandleOpacity={handleOpacityChange}
      isSidebarPropagationOpen={sidebarPropagationOpen}
      onToggleSidebar={openOperatinalFlow}
      isOpenHistoricSidebar={sidebarHistoricOpen}
      onToggleHistoricSidebar={openHistoricSidebar}
      getDfciGrid={getDfciGrid}
      dfci={dfci}
      onShowDfci={onShowDfci}
      onUpdateCurrentBounds={setCurrentBounds}
      onUpdateCurrentZoom={setCurrentZoom}
      currentZoom={currentZoom}
      currentBounds={currentBounds}
      showMeteoComponent={showMeteoComponent}
      handleMeteoComponentToggle={handleMeteoComponentToggle}
      soilMoisture={soilMoisture}
      onShowSoilMoisture={onShowSoilMoisture}
      windSpeedData={windSpeedData}
      onShowWindSpeedData={onShowWindSpeedData}
      windDirectionData={windDirectionData}
      onShowWindDirection={onShowWindDirection}
      onZoomToGrid={onZoomToGrid}
      dfciCode={dfciCode}
      setDfciCode={setDfciCode}
      showLegendComponent={showLegendComponent}
      handleLegendComponentToggle={handleLegendComponentToggle}
      baresoil={baresoil}
      onShowBareSoil={onShowBareSoil}
      broadleaf={broadleaf}
      onShowBroadleaf={onShowBroadleaf}
      pine={pine}
      onShowPine={onShowPine}
      oak={oak}
      onShowOak={onShowOak}
      mixedForest={mixedForest}
      onShowMixedForest={onShowMixedForest}
      leafAreaIndex={leafAreaIndex}
      onShowLai={onShowLai}
      vegetationHeight={vegetationHeight}
      onShowVegetationHeight={onShowVegetationHeight}
      biomass={biomass}
      onShowBiomass={onShowBiomass}
      totalFuelLoad={totalFuelLoad}
      onShowTotalFuelLoad={onShowTotalFuelLoad}
      demLevel={demLevel}
      onShowDemLevel={onShowDemLevel}
      water={water}
      onShowWater={onShowWater}
      artificielWater={artificielWater}
      onShowArtificielWater={onShowArtificielWater}
      urban={urban}
      onShowUrban={onShowUrban}
      showUserComponent={showUserComponent}
      onShowUserComponent={onShowUserComponent}
      coniferous={coniferous}
      onShowConiferous={onShowConiferous}
      shrubland={shrubland}
      onShowShrubland={onShowShrubland}
      cropland={cropland}
      onShowCropland={onShowCropland}
      grassland={grassland}
      onShowGrassland={onShowGrassland}
      majorRoads={majorRoads}
      onShowMajorRoads={onShowMajorRoads}
      showElectricalLines={electricalLines.show}
      showElectricalStations={electricalStations.show}
      onShowElectricalLines={onShowElectricalLines}
      onShowElectricalStations={onShowElectricalStations}
      selectedMeteoGetOption={selectedMeteoGetOption}
      onChangeSelectedMeteoOption={setSelectedMeteoGetOption}
      windSpeed={windSpeed}
      windDirection={windDirection}
      fuelMoisture={fuelMoisture}
      temperatureParam={temperatureParam}
      handleTemperatureChange={handleTemperatureChange}
      handleWindSpeedChange={handleWindSpeedChange}
      handleWindDirectionChange={handleWindDirectionChange}
      handleFuelMoistureChange={handleFuelMoistureChange}
      geoJsonResults={geoJsonResults}
      toggleContourBuildingsVisibility={toggleContourBuildingsVisibility}
      toggleContourVisibility={toggleContourVisibility}
      geoJsonResultAI={geoJsonResultAI}
      handleSendPolygons={handleSendPolygons}
      onChangeTime={setSelectedTime}
      selectedTime={selectedTime}
      sliderIndex={sliderIndex}
      onChangeSliderIndex={(index) => setSliderIndex(index)}
      getPolygons={getPolygons}
      getPoints={getPoints}
      currentTimeZone={currentTimeZone}
      historicFires={historicFires}
      filteredHistoricFires={filteredHistoricFires}
      getHistoricFires={getHistoricFires}
      getHistoricFiresByDate={getHistoricFiresByDate}
      onChangeHistoricFiresStartDate={setHistoricFiresStartDate}
      onChangeHistoricFiresEndDate={setHistoricFiresEndDate}
      historicFiresStartDate={historicFiresStartDate}
      historicFiresEndDate={historicFiresEndDate}
      showHistoricFiresList={showHistoricFiresList}
      showHistoricFiresByDateList={showHistoricFiresByDateList}
      onShowHistoricFiresList={onShowHistoricFiresList}
      onShowHistoricFiresByDateList={onShowHistoricFiresByDateList}
      formattedCurrentDate={formattedCurrentDate}
      formattedCurrentTime={formattedCurrentTime}
      onChangeOriginFireRef={setOriginFireRef}
      onChangeOriginFireDate={setOriginFireDate}
      onChangeOriginFireHour={setOriginFireHour}
      onChangeOriginFireLocation={setOriginFireLocation}
      originFireRef={originFireRef}
      originFireDate={originFireDate}
      originFireHour={originFireHour}
      originFireLocation={originFireLocation}
      contourHour={contourHour}
      setContourHour={setContourHour}
      contourDate={contourDate}
      setContourDate={setContourDate}
      getHistoricFiresByIds={getHistoricFiresByIds}
      handleAddFireId={handleAddFireId}
      handleRemoveFireId={handleRemoveFireId}
      selectedFiresIds={selectedFiresIds}
      operationId={operationId}
      setFilteredHistoricFiresPolygons={setFilteredHistoricFiresPolygons}
      handleReset={handleReset}
      handleExport={handleExport}
      showNavbarMenu={showNavbarMenu}
      onShowNavbarMenu={onShowNavbarMenu}
      initializeDrawTools={initializeDrawTools}
      isSmallScreen={isSmallScreen}
      toggleMapStylesSidebar={toggleMapStylesSidebar}
      showMapStylesSidebar={showMapStylesSidebar}
      logout={logout}
      railways={railways}
      onShowRailways={onShowRailways}
      gazLines={gazLines}
      onShowGazLines={onShowGazLines}
      gazRefuelingStations={gazRefuelingStations}
      onShowGazRefuelingStations={onShowGazRefuelingStations}
      precipitations={precipitations}
      onShowPrecipitations={onShowPrecipitations}
      hourlyPrecipitations={hourlyPrecipitations}
      onShowHourlyPrecipitations={onShowHourlyPrecipitations}
      deadFuel={deadFuel}
      onShowDeadFuel={onShowDeadFuel}
      onShowTemperature={onShowTemperature}
      temperature={temperature}
      relativeHumidity={relativeHumidity}
      onShowRelativeHumidity={onShowRelativeHumidity}
      meanSeaLevelPressure={meanSeaLevelPressure}
      onShowMeanSeaLevelPressure={onShowMeanSeaLevelPressure}
      onShowWindGust={onShowWindGust}
      windGust={windGust}
      onShowCloudCover={onShowCloudCover}
      cloudCover={cloudCover}
      updateProfile={updateProfile}
      updatePassword={updatePassword}
      showClock={showClock}
      setShowClock={setShowClock}
      showCursorCoords={showCursorCoords}
      setShowCursorCoords={setShowCursorCoords}
      fireStationsData={fireStationsData}
      onShowFireStations={onShowFireStations}
      trainStationsData={trainStationsData}
      onShowTrainStations={onShowTrainStations}
      onShowUserHistoricEvents={onShowUserHistoricEvents}
      userHistoricEventsList={userHistoricEventsList}
      getUserHistoricEvents={getUserHistoricEvents}
      getHistoricEventPolygons={getHistoricEventPolygons}
      handleEventSelect={handleEventSelect}
      setLoadingUserEvents={setLoadingUserEvents}
      modelErrorMessage={modelErrorMessage}
      handleResetOpacity={handleResetOpacity}
      reducedWindow={reducedWindow}
      onReduceLayersWindow={() =>
        setReducedWindow((prev) => ({
          ...prev,
          layersComponent: !reducedWindow.layersComponent,
        }))
      }
      onReduceWeatherWindow={() =>
        setReducedWindow((prev) => ({
          ...prev,
          weatherComponent: !reducedWindow.weatherComponent,
        }))
      }
      onReduceEventCreationWindow={() =>
        setReducedWindow((prev) => ({
          ...prev,
          eventCreationComponent: !reducedWindow.eventCreationComponent,
        }))
      }
      onReduceHistoricFiresnWindow={() =>
        setReducedWindow((prev) => ({
          ...prev,
          historicFiresComponent: !reducedWindow.historicFiresComponent,
        }))
      }
      onReduceLegendWindow={() =>
        setReducedWindow((prev) => ({
          ...prev,
          legendComponent: !reducedWindow.legendComponent,
        }))
      }
      onReduceActiveFiresWindow={() =>
        setReducedWindow((prev) => ({
          ...prev,
          activeFiresComponent: !prev.activeFiresComponent,
        }))
      }
      currentLoadingMessage={currentLoadingMessage}
      setCurrentLoadingMessage={setCurrentLoadingMessage}
      buildingsLoading={buildingsLoading}
      weatherRequestDate={weatherRequestDate}
      weatherRequestStartHour={weatherRequestStartHour}
      setWeatherRequestDate={setWeatherRequestDate}
      setWeatherRequestStartHour={setWeatherRequestStartHour}
      selectedStyle={selectedStyle}
      setSelectedStyle={setSelectedStyle}
      timelineKey={timelineKey}
      weatherCountry={weatherCountry}
      setWeatherCountry={setWeatherCountry}
      drawRef={drawRef}
      closeAllLayers={closeAllLayers}
      predictionHours={predictionHours}
      setPredictionHours={setPredictionHours}
      timeStep={timeStep}
      setTimeStep={setTimeStep}
      onShowAllBuildings={showAllBuildings}
      onHideAllBuildings={hideAllBuildings}
      loadingEventId={loadingEventId}
      geoJsonInputContour={geoJsonInputContour}
      vegetationCountry={vegetationCountry}
      setVegetationCountry={setVegetationCountry}
      selectedVegDepartment={selectedVegDepartment}
      setSelectedVegDepartment={setSelectedVegDepartment}
      selectedWeatherDepartment={selectedWeatherDepartment}
      setSelectedWeatherDepartment={setSelectedWeatherDepartment}
      activeFires={activeFires}
      onShowActiveFires={onShowActiveFires}
      showActiveFiresPanel={showActiveFiresPanel}
      onToggleActiveFiresPanel={onToggleActiveFiresPanel}
      activeFiresStartDate={activeFiresStartDate}
      onChangeActiveFiresStartDate={setActiveFiresStartDate}
      activeFiresEndDate={activeFiresEndDate}
      onChangeActiveFiresEndDate={setActiveFiresEndDate}
      onApplyActiveFiresFilters={onApplyActiveFiresFilters}
      getActiveFires={getActiveFires}
      hasVisibleHistoricFires={hasVisibleHistoricFires}
      activeFiresStartTime={activeFiresStartTime}
      onChangeActiveFiresStartTime={setActiveFiresStartTime}
      activeFiresEndTime={activeFiresEndTime}
      onChangeActiveFiresEndTime={setActiveFiresEndTime}
      resetLocationTrigger={resetLocationTrigger}
      activeFiresBuckets={activeFiresBuckets}
      activeFiresCursor={activeFiresCursor}
      onChangeActiveFiresCursor={setActiveFiresCursor}
      activeFiresPreset={activeFiresPreset}
      onChangeActiveFiresPreset={setActiveFiresPreset}
      activeFiresDisplayStartDate={activeFiresDisplayStartDate}
      onChangeActiveFiresDisplayStartDate={setActiveFiresDisplayStartDate}
      activeFiresDisplayEndDate={activeFiresDisplayEndDate}
      onChangeActiveFiresDisplayEndDate={setActiveFiresDisplayEndDate}
      defaultHistoricEndDate={defaultHistoricEndDate}
      anyWeatherVisible={anyWeatherVisible}
    />
  );
};
export default MapScreen;
